/**
 * agentes.js — Catálogo de agentes de automatización
 *
 * Dos niveles:
 *   1. Catálogo global (colección `agentes`): define el agente, su área, dashboard,
 *      secciones que ejecuta, instrucciones, config IA.
 *   2. Config por empresa (subcol `empresas/{empId}/agentesConfig`): activa/desactiva,
 *      modo (auto/manual), destinatario de tareas, última ejecución.
 *
 * Schema del agente (colección `agentes`):
 *   - nombre: string
 *   - descripcion: string
 *   - tipo: 'plataforma' | 'usuario'
 *       plataforma = corre clasificador y crea tareas automáticamente
 *       usuario    = se asigna a tareas como un miembro del equipo y las ejecuta
 *   - area: string (publicidad, ventas, etc.)
 *   - dashboardId: string|null (publicidad-ml, ventas-ml, etc. — null para User Agents)
 *   - secciones: string[] (pausar, activar, optimizar, etc.)
 *   - instrucciones: string (texto libre editable por admin)
 *   - activo: bool
 *   - icono: string (emoji)
 *   - ia: { usaIA: bool, modelo: string|null, prompt: string|null, temperatura: number|null }
 *   - configCompetitividad: { precio: number, cuotas: number, tiempo: number,
 *       confianza: number, costoEnvio: number, marca: number, promocion: number } | null
 *       — pesos (%) que usa el reporte de competencia para calcular el score global.
 *         Deben sumar 100. Si null, el reporte usa pesos dinámicos por defecto.
 *   - umbrales: { [seccion]: { tolerancia_pct?, volumen_minimo?, ventana_dias?, ... } } | null
 *       — Umbrales editables del evaluador determinístico por sección. Cada sección
 *         tiene sus propios parámetros (ej: tolerancia_pct, volumen_minimo, ventana_dias).
 *         El evaluador lee de acá; defaults vienen del seed. Editable desde UI sin redeploy.
 *         Patrón usado por agente-postventa-ml (5 secciones). Genérico para otros agentes.
 *   - creadoEn, actualizadoEn: timestamps
 *
 * Schema de config por empresa (subcol `empresas/{empId}/agentesConfig/{agenteId}`):
 *   - agenteId: string (FK a agentes)
 *   - activo: bool
 *   - modo: 'manual' | 'automatico'
 *   - destinatarioUserId: string (a quién se asignan las tareas)
 *   - ultimaEjecucion: timestamp|null
 *   - resultadoUltimaEjecucion: { tareasCreadas: number, fecha: string } | null
 *   - creadoEn, actualizadoEn: timestamps
 */

import {
  collection, doc, getDocs, getDocsFromServer, getDoc, setDoc, updateDoc, deleteDoc,
  serverTimestamp, query, where, orderBy,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
// Flywheel (14-jul-2026, docs/flywheel-mercadolibre.md): pares de acciones
// opuestas — el lado PLATA (pausar/subir precio) le gana al lado INVERSIÓN.
import { TIPOS_OPUESTOS, TIPOS_LADO_PLATA } from './catalogoTiposTarea'
import { ordenCanonicaAShapeCascada } from '@modules/mercadolibre/lib/ordenParaCascada'
// Paridad de universo con el dashboard de Contribución Neta (12-jul-2026):
// el dashboard clasifica solo órdenes facturables — el loader espeja eso
// cuando el registry lo pide (soloFacturables).
import { esFacturable } from '@modules/mercadolibre/hooks/useOrdenesPeriodo'
import { pubCanonicaALegacy } from '@modules/mercadolibre/lib/publicacionesLegacyShape'
// Central de Promociones (12-jul-2026): canónicas → shapes legacy del dashboard.
import { campanaPromoALegacyMaster, campanasItemsAPromosPorItem, pubCanonicaACentralPromos } from '@modules/mercadolibre/lib/centralPromosCanonicoShape'
import { getDefaultsParametros } from '@modules/plataforma/lib/reglasDecisionDefaults'
import { categoriasDistintas } from './clasificadores/tendencias'
import { createLogger } from '@core/lib/logger'
import { getFullVigente } from '@core/lib/catalogoFull'
import { adsCanonicosALegacy } from '@modules/mercadolibre/lib/publicidadLegacyShape'
import { reclamoCanonicoALegacy, devolucionCanonicaALegacy } from '@modules/mercadolibre/lib/postventaLegacyShape'

// ─── `diasVentas`: qué significa el 0, y por qué hay una constante ─────────
// El registry (`clasificadores/index.js`) declara `diasVentas` por dashboard y
// el 0 significa "sin ventana de ventas" — está escrito en el propio registry
// (`'ml-tendencias'`: "Sin ventana de ventas, el motor cruza títulos, no
// órdenes") y en `docs/DASHBOARD_PREGUNTAS.md`. Hasta el report
// bssopFE0UcL3dujEWO8k eso NO se cumplía: los tres lectores del campo usaban
// `|| 30`, que se come el 0 por falsy, así que SEIS clasificadores de
// producción que declaran 0 leían igual 30 días de `ordenes`.
//
// La constante existe para que el default sea UN número con nombre en vez de
// tres literales sueltos: los tres lectores lo expresan distinto —el loader
// saltea la lectura, el corte de reclamos queda en `null`, la cascada exige
// ventana— pero el número al que caen cuando el campo NO viene es el mismo.
const DIAS_VENTANA_DEFAULT = 30

/**
 * La ventana (en días) con la que corre la cascada de rentabilidad.
 *
 * Acá el `0` NO significa lo mismo que en el loader. En el loader significa
 * "no cargues ventas" y se honra salteando la lectura; acá significaría correr
 * la cascada SIN rango, y eso es el bug que el comentario del call site dice
 * que se arregló: la Capa 1 escaneando toda la historia de la empresa en cada
 * corrida. Por eso un `0` (o un campo ausente, o un negativo) cae al default en
 * vez de propagarse.
 *
 * Vive como función exportada y no como una línea dentro de `ejecutarAgente`
 * para que la decisión tenga test propio: `ejecutarAgente` es un orquestador
 * sin harness, y una semántica enterrada ahí adentro es una semántica que el
 * próximo refactor puede cambiar a `??` sin que nada se ponga rojo.
 *
 * @param {number|undefined|null} diasVentas — lo que declara el registry.
 * @returns {number} días de ventana, siempre > 0.
 */
export function ventanaDeCascada(diasVentas) {
  return diasVentas > 0 ? diasVentas : DIAS_VENTANA_DEFAULT
}

/**
 * Cuánto espera el cliente a la cascada de rentabilidad. Es UN hecho con dos
 * lectores —el `httpsCallable` y `cascadaEntraEnPresupuesto`—: con el número
 * escrito dos veces, bajar uno deja al otro decidiendo con un techo que ya no
 * es el de la llamada (la clase que cerraron #4123 y #4750).
 */
export const TIMEOUT_CASCADA_RENT_MS = 180000

/**
 * ¿Entra la cascada de rentabilidad en lo que le queda a la corrida?
 * (report emCvFu4PIotPBsN21ckT)
 *
 * La decisión es BINARIA a propósito: o corre con su techo entero o no se
 * llama. Acortar el timeout del cliente no acorta el trabajo del servidor
 * —la cascada sigue escribiendo hasta su propio presupuesto— y el clasificador
 * leería datos a medio escribir reportados como "degradada": se cambiaría una
 * pérdida ruidosa por una silenciosa.
 *
 * `deadlineMs` es un epoch absoluto. Sin presupuesto (null/undefined, el
 * navegador) corre siempre, como antes. Un `0` heredado se lee como "no queda
 * nada", no como "sin deadline" (por eso no se mira la verdad del valor), y un
 * valor que no es número no se interpreta: falla cerrado, se saltea declarado.
 *
 * @param {number|null|undefined} deadlineMs
 * @param {number} [ahoraMs]
 * @returns {boolean}
 */
export function cascadaEntraEnPresupuesto(deadlineMs, ahoraMs = Date.now()) {
  if (deadlineMs == null) return true
  if (!Number.isFinite(deadlineMs)) return false
  return deadlineMs - ahoraMs >= TIMEOUT_CASCADA_RENT_MS
}

// ═══════════════════════════════════════
// Catálogo global de agentes
// ═══════════════════════════════════════

const agentesRef = () => collection(db, COLLECTIONS.AGENTES)
const agenteRef = (id) => doc(db, COLLECTIONS.AGENTES, id)

/** Listar todos los agentes (opcionalmente solo activos) */
export async function listarAgentes({ soloActivos = false } = {}) {
  const snap = await getDocs(agentesRef())
  let list = snap.docs.map(docConId)
  if (soloActivos) list = list.filter(a => a.activo !== false)
  list.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
  return list
}

/** Obtener un agente por ID */
export async function obtenerAgente(id) {
  const snap = await getDoc(agenteRef(id))
  return snap.exists() ? docConId(snap) : null
}

/** Obtener agente por dashboardId */
export async function obtenerAgentePorDashboard(dashboardId) {
  const snap = await getDocs(query(agentesRef(), where('dashboardId', '==', dashboardId)))
  if (snap.empty) return null
  const d = snap.docs[0]
  return docConId(d)
}

/** Crear o actualizar un agente */
export async function upsertAgente(id, data) {
  const ref = id ? agenteRef(id) : doc(agentesRef())
  const existing = id ? (await getDoc(ref)).exists() : false

  const payload = {
    nombre: data.nombre || '',
    descripcion: data.descripcion || '',
    tipo: data.tipo || 'plataforma', // 'plataforma' | 'usuario'
    area: data.area || '',
    dashboardId: data.dashboardId || null,
    secciones: data.secciones || [],
    instrucciones: data.instrucciones || '',
    activo: data.activo !== false,
    icono: data.icono || '🤖',
    // 'agrupado' = una tarea por sección con todos los items como recursos[]
    // 'individual' = una tarea por cada item de la sección
    modoTareas: data.modoTareas === 'individual' ? 'individual' : 'agrupado',
    ia: {
      usaIA: data.ia?.usaIA || false,
      modelo: data.ia?.modelo || null,
      prompt: data.ia?.prompt || null,
      temperatura: data.ia?.temperatura ?? null,
    },
    configCompetitividad: data.configCompetitividad || null,
    umbrales: data.umbrales || null,
    actualizadoEn: serverTimestamp(),
  }

  if (!existing) {
    payload.creadoEn = serverTimestamp()
  }

  await setDoc(ref, payload, { merge: true })
  return ref.id
}

/** Desactivar un agente */
export async function desactivarAgente(id) {
  await updateDoc(agenteRef(id), { activo: false, actualizadoEn: serverTimestamp() })
}

/** Toggle activo/inactivo de un agente */
export async function toggleAgenteActivo(id, nuevoEstado) {
  await updateDoc(agenteRef(id), { activo: nuevoEstado, actualizadoEn: serverTimestamp() })
}

// ═══════════════════════════════════════
// Config de agente por empresa
// ═══════════════════════════════════════

const configRef = (empresaId, agenteId) =>
  doc(db, COLLECTIONS.EMPRESAS, empresaId, 'agentesConfig', agenteId)

const configColRef = (empresaId) =>
  collection(db, COLLECTIONS.EMPRESAS, empresaId, 'agentesConfig')

/** Listar configs de agentes para una empresa */
export async function listarAgentesEmpresa(empresaId) {
  const snap = await getDocs(configColRef(empresaId))
  return snap.docs.map(docConId)
}

/** Obtener config de un agente para una empresa */
export async function obtenerAgenteEmpresa(empresaId, agenteId) {
  const snap = await getDoc(configRef(empresaId, agenteId))
  return snap.exists() ? docConId(snap) : null
}

/** Activar/configurar un agente en una empresa */
export async function configurarAgenteEmpresa(empresaId, agenteId, config) {
  const ref = configRef(empresaId, agenteId)
  const existing = (await getDoc(ref)).exists()

  const payload = {
    agenteId,
    activo: config.activo !== false,
    modo: config.modo || 'manual',
    destinatarioUserId: config.destinatarioUserId || null,
    // Fase 2 (16-jul-2026): programación días+horas del modo automático —
    // la lee el runner backend (functions/agentesAutoRunner). Solo se toca
    // si el caller la manda (merge: la config vieja no se pisa con undefined).
    ...(config.programacion !== undefined ? { programacion: config.programacion || null } : {}),
    actualizadoEn: serverTimestamp(),
  }

  if (!existing) {
    payload.creadoEn = serverTimestamp()
    payload.ultimaEjecucion = null
    payload.resultadoUltimaEjecucion = null
  }

  await setDoc(ref, payload, { merge: true })
}

/** Desactivar un agente en una empresa */
export async function desactivarAgenteEmpresa(empresaId, agenteId) {
  await updateDoc(configRef(empresaId, agenteId), {
    activo: false,
    actualizadoEn: serverTimestamp(),
  })
}

/**
 * Activar por DEFAULT los agentes de detección para una empresa recién creada.
 * ⭐ Decisión Mariano 14-jul-2026: empresa nueva = TODOS los agentes de
 * detección prendidos en modo manual (el operador después ajusta destinatario
 * o apaga lo que no aplique). Solo agentes de plataforma ACTIVOS con
 * dashboardId (los de detección — mismo criterio que el seed de terminal).
 * Idempotente: no pisa configs existentes.
 */
export async function activarAgentesDeteccionDefault(empresaId) {
  const agentes = await listarAgentes({ soloActivos: true })
  const deteccion = agentes.filter(a => a.dashboardId)
  let activados = 0
  let yaExistian = 0
  for (const a of deteccion) {
    const ref = configRef(empresaId, a.id)
    const snap = await getDoc(ref)
    if (snap.exists()) { yaExistian++; continue }
    await setDoc(ref, {
      agenteId: a.id,
      activo: true,
      modo: 'manual',
      destinatarioUserId: null,
      ultimaEjecucion: null,
      creadoEn: serverTimestamp(),
      actualizadoEn: serverTimestamp(),
    })
    activados++
  }
  return { activados, yaExistian, total: deteccion.length }
}

/** Registrar ejecución del agente */
export async function registrarEjecucion(empresaId, agenteId, resultado) {
  await updateDoc(configRef(empresaId, agenteId), {
    ultimaEjecucion: serverTimestamp(),
    resultadoUltimaEjecucion: {
      tareasCreadas: resultado.tareasCreadas || 0,
      fecha: new Date().toISOString(),
      secciones: resultado.secciones || [],
    },
    actualizadoEn: serverTimestamp(),
  })
}

// ═══════════════════════════════════════
// Ejecución del agente
// ═══════════════════════════════════════

import { getClasificador } from './clasificadores'
import { recursoContribucionNeta } from './clasificadores/contribucionNeta'
import { obtenerTipoTarea } from './mapeoTareas'
// FIX 10-jul-2026 (duplicado manual↔agente): el codigo del tipo MAPEADO es la
// clave real de dedupe — el modal persiste tipoTareaResuelto.codigo, no el
// default. El agente tiene que resolver IGUAL (mapeo → codigo, default fallback).
import { getTipoTarea, getTipoTareaPorCodigo } from './tiposTarea'
import { crearAccionable, upsertRecursosEnTarea, exigirTareasOperables } from './tareas'
import { recursoConOrigen } from './mergeRecursos'
// Tipos que acumulan hallazgos de varias fuentes en una sola tarea por
// publicación (merge por origen). P&R, Opiniones y Postventa → modificar_publicacion.
import { SECCION_DEFAULTS } from './seccionDefaults'
import { getFunctions, httpsCallable } from 'firebase/functions'
import firebaseApp from './firebase'
import { mergearConfig as mergearImpuestos } from '@modules/rentabilidad/hooks/useImpuestosEmpresa'
import { claveRollup, CANAL_ML } from './clavesRollup'
import { inicioVentanaLocal } from './diaLocal'

const logLoader = createLogger('agentesLoader')

// ── Ids de canal sin interceptor (report K1h7byclDvpEmH1kklL1) ──────────────
// `colecciones` de un clasificador son ids que el loader resuelve contra la
// canónica, cada uno con su interceptor en `cargarColeccion`. Un id con prefijo
// de canal que llega a la rama genérica se consultaría LITERAL, y el hook de §2
// (`scripts/check-canonical-collections.sh`, MEGA_PATTERN) no lo ve: sólo
// reconoce literales en `collection(...)`, y acá el nombre viaja en una
// variable. Los prefijos son los de canal de MEGA_PATTERN (la paridad la ata
// `agentes.cargarDatos.test.js`); quedan afuera los suyos que no son canal
// (`milbrands`, `cliente`, `empresa`).
export const PREFIJO_CANAL_SIN_INTERCEPTOR = /^(ml|tn|mp|shopify|amzn|amazon|meli)[A-Z]/
export const MOTIVO_CANAL_SIN_INTERCEPTOR = 'id de canal sin interceptor: declararlo no lee nada — sumar un interceptor contra la canónica'

// ── Degradación silenciosa del loader (report f0MNIPLik) ────────────────────
// Una "fuente" es una lectura FAIL-SOFT: ante un permiso denegado, una red
// caída o un rate limit devuelve vacío y deja al clasificador con MENOS datos
// sin que nada lo diga. Las lecturas duras no pasan por acá: si revientan
// tiran la corrida y eso ya se ve en `ok: false`.
//
// El registro viaja DENTRO del valor memoizado (ver `memo`): dos agentes que
// comparten la promesa cacheada declaran LOS DOS la fuente caída. Al lado del
// cache la declararía sólo el primero.
//
// Las fuentes intentadas son el denominador: sin él, "0 degradaciones" no se
// distingue de "no se midió nada" (report 5db6CWnHwjia48EbJ9SF).
export function registroDeLecturas() {
  const degradaciones = []
  // NOMBRES y no un contador: con el cache compartido la misma fuente la
  // intentan varios agentes, y sumar contadores daría un denominador inflado
  // contra un numerador deduplicado ("1 de 40" por una sola fuente caída).
  const fuentes = []
  return {
    /**
     * Corre `fn`; si falla, declara la fuente degradada y devuelve `fallback`.
     * `fuente` es el nombre estable de la lectura, agnóstico de canal: es lo
     * que ve el operador en el aviso.
     * @returns {Promise<[any, {fuente:string,motivo:string}|null]>}
     */
    async leer(fuente, fn, fallback) {
      fuentes.push(fuente)
      try {
        return [await fn(), null]
      } catch (e) {
        const degradacion = { fuente, motivo: motivoDeFallo(e) }
        degradaciones.push(degradacion)
        return [fallback, degradacion]
      }
    },
    resultado: () => ({ degradaciones, fuentes }),
  }
}

/** Une registros deduplicando por `fuente`: numerador y denominador con el
 *  mismo criterio. */
export function unirDegradaciones(partes) {
  const degradaciones = []
  const vistas = new Set()
  const fuentes = new Set()
  for (const p of partes) {
    for (const f of (p?.fuentes || [])) fuentes.add(f)
    for (const d of (p?.degradaciones || [])) {
      if (vistas.has(d.fuente)) continue
      vistas.add(d.fuente)
      degradaciones.push(d)
    }
  }
  return { degradaciones, fuentes: [...fuentes] }
}

/**
 * La parte de `unirDegradaciones` que declara el clasificador
 * (`resultado.lecturas`), validada. El clasificador es código de un vertical y
 * el aviso lo lee el operador: una degradación sin `fuente` haría que
 * `esDegradada` diera true y `tituloDegradacion` cayera en «sin detalle», y
 * una fuente que no esté en `fuentes` rompería «N de M». Así que se normaliza
 * acá, fallando hacia el aviso: lo malformado se declara con el nombre
 * `clasificador` en vez de descartarse en silencio.
 */
export function lecturasDelClasificador(resultado) {
  const crudas = resultado?.lecturas?.degradaciones
  if (!Array.isArray(crudas) || crudas.length === 0) return { degradaciones: [], fuentes: [] }
  const degradaciones = crudas.map(d => ({
    fuente: typeof d?.fuente === 'string' && d.fuente.trim() ? d.fuente : 'clasificador',
    motivo: String(d?.motivo ?? 'sin-detalle').slice(0, 200),
  }))
  return { degradaciones, fuentes: degradaciones.map(d => d.fuente) }
}

// El CÓDIGO diagnostica más que el mensaje (permission-denied ≠ unavailable ≠
// deadline-exceeded) y el mensaje crudo puede traer texto del server: se acota.
// Exportada desde el report lGBhHodM: la flota encogida NO entra como
// `degradaciones` (no es una fuente de datos, y mezclarla ensuciaría el
// denominador "N de M fuente(s)"), así que `agenteGeneral` arma su propio
// `{ motivo }` y necesita el MISMO diagnóstico — no una segunda versión.
export function motivoDeFallo(e) {
  const code = e?.code ? String(e.code) : ''
  const msg = e?.message ? String(e.message) : String(e ?? 'sin-detalle')
  return (code ? `${code}: ${msg}` : msg).slice(0, 200)
}

// ── ¿Esta corrida miente? UNA derivación, no un predicado por consumidor ────
// report lGBhHodM. Hasta acá cada lector rearmaba la fórmula por su cuenta:
// `agentesAutoRunner.js` con `conError > 0 || degradaciones.length > 0`, y
// `tituloDegradacion` con `degradaciones` + `agentesConError`. Esa forma
// ENUMERATIVA es el bug, no el término que falta: el comentario del runner
// confiesa que la misma línea ya fue "false por construcción y no por
// medición" una vez, se parchó sumando un término, y volvía a serlo con el
// siguiente. Una fórmula, todos los consumidores.
//
// `degradado` se honra como lo que es —una derivación CACHEADA que el runner
// persiste— y no como la compuerta de entrada: los `ultimoResultadoAuto`
// viejos lo traen en `true` sin detalle, y su mensaje tiene que seguir
// saliendo. Ese `||` es compatibilidad hacia atrás, no cinturón redundante.
//
// Sirve a los tres shapes del mismo contrato: el reporte de `corridasDeteccion`,
// el `ultimoResultadoAuto` del runner, y el resultado suelto de `ejecutarAgente`
// (que no trae `porAgente`). Lo que no está, no cuenta: nunca al revés.
export function esDegradada(res) {
  if (!res) return false
  if (res.degradado === true) return true
  const conError = res.agentesConError ?? (res.porAgente || []).filter(a => !a.ok).length
  return (res.degradaciones?.length || 0) > 0
    || conError > 0
    || (res.configNoLeida?.length || 0) > 0
    || (res.noCorridosPorPresupuesto?.length || 0) > 0
    || !!res.autoSeed?.error
}

/**
 * Qué se perdió en una corrida degradada — NO un conteo que puede ser cero
 * (report f0MNIPLik). El tooltip decía "Corrió pero con N agente(s) con error
 * adentro" y `agentesConError` sigue contando sólo agentes que TIRARON: con
 * degradaciones del loader y cero agentes caídos, la pantalla habría dicho
 * literal "con 0 agente(s) con error adentro" — el marcador que da cero por
 * construcción que ya pagamos una vez (report 5db6CWnHwjia48EbJ9SF).
 *
 * Vivía exportada desde un componente de `modules/gestion` (report lGBhHodM).
 * Se mudó al horizontal porque la necesita un SEGUNDO vertical, y un vertical
 * importando de otro es la excepción con trigger de repago del ADR
 * 2026-08-16, no el caso limpio. Molde del move: `describirProgramacion`
 * (`programacionAgentes.js`), helper de texto puro del mismo dominio que ya
 * vive en el horizontal y que `headlessAgentes.js` re-exporta.
 */
export function tituloDegradacion(res) {
  if (!esDegradada(res)) return ''
  const partes = []
  const fuentes = (res.degradaciones || []).map(d => d.fuente).filter(Boolean)
  if (fuentes.length > 0) {
    // Con denominador: "1 de 14" dice algo que "1" no dice.
    const total = res.fuentesIntentadas ? ` de ${res.fuentesIntentadas}` : ''
    partes.push(`Corrió sin ${fuentes.length}${total} fuente(s) de datos: ${fuentes.join(', ')}`)
  }
  // La flota encogida tiene su PROPIO denominador: cuántos agentes debían
  // correr, no cuántas fuentes se intentaron. Mezclarlos ensuciaría el
  // "N de M fuente(s)" con cosas que no son lecturas de datos.
  const noLeidos = res.configNoLeida || []
  if (noLeidos.length > 0) {
    const total = res.agentesEnFlota ? ` de ${res.agentesEnFlota}` : ''
    partes.push(`${noLeidos.length}${total} agente(s) no corrieron porque no se pudo leer su configuración: ${noLeidos.map(a => a.nombre || a.agenteId).join(', ')}`)
  }
  // Mismo denominador que la de arriba, otra causa y otra remediación: estos
  // no fallaron, no les tocó turno antes de que se acabara el presupuesto.
  const sinTurno = res.noCorridosPorPresupuesto || []
  if (sinTurno.length > 0) {
    const total = res.agentesEnFlota ? ` de ${res.agentesEnFlota}` : ''
    partes.push(`${sinTurno.length}${total} agente(s) no corrieron porque se agotó el tiempo de la corrida: ${sinTurno.map(a => a.nombre || a.agenteId).join(', ')}`)
  }
  if (res.agentesConError > 0) partes.push(`${res.agentesConError} agente(s) con error adentro`)
  if (res.autoSeed?.error) partes.push('el auto-seed del catálogo falló')
  // Degradada sin detalle: la corrida es vieja (se guardó antes de que el
  // detalle existiera). Se dice eso, no un cero.
  return partes.length > 0 ? partes.join(' · ') : 'Corrió degradada (sin detalle de qué se perdió)'
}

/**
 * ejecutarAgente — Ejecuta un agente para una empresa
 *
 * Flujo:
 *   1. Lee el agente y su config de empresa
 *   2. Busca el clasificador por dashboardId
 *   3. Carga datos de Firestore (ads, publicaciones, ventas)
 *   4. Corre el clasificador → obtiene secciones con items. Si el clasificador
 *      no pudo calcular algo por un insumo faltante, lo declara en
 *      `resultado.lecturas` = `{ fuentes: string[], degradaciones: [{fuente, motivo}] }`
 *      (mismo shape que `registroDeLecturas().resultado()`), sólo cuando falta;
 *      se une con las degradaciones del loader (ver `lecturasDelClasificador`).
 *   5. Por cada sección con items, crea una tarea agrupada
 *   6. Registra la ejecución
 *
 * @param {string} empresaId
 * @param {string} agenteId
 * @param {Object} options
 * @param {string} options.ejecutadoPor - userId de quien lo ejecuta (o 'sistema' para auto)
 * @param {number|null} [options.deadlineMs] - epoch hasta el que puede correr (lo pasa el
 *   runner, por trabajo). null = sin presupuesto: el navegador no lo pasa.
 * @returns {Object} { tareasCreadas, secciones: [{ seccion, items, tareaId }] }
 */
export async function ejecutarAgente(empresaId, agenteId, { ejecutadoPor = 'sistema', cache = null, deadlineMs = null } = {}) {
  // Array de logs estructurados — se devuelve al caller para mostrar en UI
  // (sin depender de la consola del browser). Cada entry: { nivel, msg, data? }
  // nivel ∈ 'info' | 'warn' | 'error'.
  const logs = []
  const log = (nivel, msg, data) => {
    const entry = { ts: Date.now(), nivel, msg, ...(data !== undefined ? { data } : {}) }
    logs.push(entry)
    const line = `[ejecutarAgente] ${msg}` + (data !== undefined ? ` ${JSON.stringify(data)}` : '')
    if (nivel === 'error') console.error(line)
    else if (nivel === 'warn') console.warn(line)
    else console.log(line)
  }

  // Fuentes fail-soft de ESTE agente (las del loader llegan con los datos).
  // Ver `registroDeLecturas`: sin esto la corrida se reporta limpia mientras
  // el clasificador decide con otros números.
  const regAgente = registroDeLecturas()

  log('info', `START`, { empresaId, agenteId, ejecutadoPor })

  // 0. ADR 2026-10-08: sin Tareas operable (plan + solapa Módulos) no corre
  // ningún agente — antes de leer nada y antes de la cascada de rentabilidad.
  await exigirTareasOperables(empresaId)

  // 1. Leer agente y config
  const agente = await obtenerAgente(agenteId)
  if (!agente) throw new Error(`Agente ${agenteId} no encontrado`)
  // El interruptor del catálogo (`agentes/{id}.activo`) prometía apagar y
  // acá no se miraba (regla del 22-ago). El runner los filtra en su
  // descubrimiento; esto cubre el botón manual.
  if (agente.activo === false) throw new Error(`El agente ${agente.nombre || agenteId} está apagado en la plataforma`)
  log('info', 'agente leído', { area: agente.area, dashboardId: agente.dashboardId, secciones: agente.secciones })

  const config = await obtenerAgenteEmpresa(empresaId, agenteId)
  if (!config || !config.activo) throw new Error(`Agente ${agenteId} no está activo para empresa ${empresaId}`)
  log('info', 'config empresa OK', { activo: config.activo, modo: config.modo, destinatario: config.destinatarioUserId || null })

  // 2. Buscar clasificador
  const clasificadorDef = getClasificador(agente.dashboardId)
  if (!clasificadorDef) throw new Error(`No hay clasificador para dashboard ${agente.dashboardId}`)
  log('info', 'clasificador encontrado', { dashboardId: agente.dashboardId, colecciones: clasificadorDef.colecciones })

  // 2.5. Si el agente es del módulo Rentabilidad, persistimos la rentabilidad
  // de las órdenes ANTES de clasificar. El callable `calcularRentabilidadEmpresa`
  // corre la cascada Capa 1 (orden) → Capa 2 (publicación) → Capa 3 (producto)
  // en modo incremental (solo lo no calculado o con fórmula vieja). Sin este
  // paso, el agente solo procesa órdenes con `_rentabilidad.estado === 'calculada'`
  // y se perdería todo lo nuevo o pendiente. La cascada persiste en la canónica
  // `ordenes.rentabilidad` (Bloque 5), así el clasificador después lo lee normal.
  if (agente.area === 'rentabilidad' && !cascadaEntraEnPresupuesto(deadlineMs)) {
    // No se llama: no entra entera (`cascadaEntraEnPresupuesto`). El salteo
    // pasa IGUAL por `regAgente.leer` —nunca un return antes— para que la
    // fuente cuente en el denominador y quede declarada: un salteo que no
    // pasa por el registro da `degradado: false` por construcción. Y con
    // nombre propio, no `cascada-rentabilidad`: la remediación es la opuesta
    // (acá no se lanzó nada y los datos están intactos) y el aviso se
    // deduplica por fuente, así que compartirla taparía una cascada rota.
    await regAgente.leer('cascada-rentabilidad-presupuesto', async () => {
      throw new Error(`no entra en el presupuesto de la corrida (necesita ${TIMEOUT_CASCADA_RENT_MS / 1000} s)`)
    }, null)
    log('warn', 'cascada rentabilidad salteada: no entra en el presupuesto de la corrida. Continúo con la cascada runtime del clasificador.')
  } else if (agente.area === 'rentabilidad') {
    const [, degCascada] = await regAgente.leer('cascada-rentabilidad', async () => {
      const functions = getFunctions(firebaseApp, 'us-central1')
      // 12-jul-2026 (fix de fondo de paridad): timeout explícito — el default
      // del SDK (70s) abortaba en cliente mientras la cascada (hasta 540s)
      // seguía escribiendo server-side y el loader leía datos a mitad de
      // escritura. Y RANGO de la ventana del clasificador: sin desde/hasta
      // la Capa 1 escaneaba TODA la historia de la empresa en cada corrida.
      const calcularRentFn = httpsCallable(functions, 'calcularRentabilidadEmpresa', { timeout: TIMEOUT_CASCADA_RENT_MS })
      // El `0` de `diasVentas` significa otra cosa acá que en el loader — el
      // porqué y su test están en `ventanaDeCascada`.
      const diasCascada = ventanaDeCascada(clasificadorDef.diasVentas)
      const desdeCascada = inicioVentanaLocal(diasCascada)
      log('info', 'corriendo cascada de rentabilidad antes de clasificar', { modo: 'incremental', ventanaDias: diasCascada })
      const t0 = Date.now()
      const r = await calcularRentFn({
        empresaId,
        modo: 'incremental',
        desde: desdeCascada.toISOString(),
        hasta: new Date().toISOString(),
      })
      log('info', `cascada rentabilidad terminada en ${Date.now() - t0}ms`, r?.data || null)
      return null
    }, null)
    // No abortamos si la cascada falla — el clasificador sigue con lo que haya
    // persistido o con el cálculo simplificado. Pero ahora la falla QUEDA en el
    // resultado de la corrida, no sólo en el logger. El motivo trae el code,
    // que es lo que diagnostica: permission-denied = falta el permiso
    // rentabilidad.simular; deadline-exceeded = la cascada sigue server-side.
    if (degCascada) {
      log('warn', `cascada rentabilidad falló [${degCascada.motivo}]. Continúo con la cascada runtime del clasificador.`)
    }
  }

  // 3. Cargar datos de Firestore
  // `cache` (agente general, 16-jul-2026): Map compartido de la corrida —
  // carga única de colecciones entre agentes. null = corrida individual.
  const datos = await cargarDatosParaClasificador(empresaId, clasificadorDef, { cache })

  // Viajan en un campo con `_` (molde `_acosCfg`): la firma de
  // `cargarDatosParaClasificador` no cambia y el clasificador —función pura
  // sobre datos de negocio— no las ve. Se sacan en el borde que las consume.
  const degLoader = datos._degradaciones || { degradaciones: [], fuentes: [] }
  delete datos._degradaciones

  // Debug: cuántas ventas tienen _rentabilidad calculada. Si es 0, el
  // clasificador de Contribución Neta va a devolver secciones vacías.
  if (agente.area === 'rentabilidad') {
    const ventas = datos.ventas30d || []
    const conRentCalc = ventas.filter(v => v._rentabilidad && v._rentabilidad.estado === 'calculada').length
    log('info', 'ventas cargadas', { total: ventas.length, conRentPersistida: conRentCalc, simplificadaEsperada: ventas.length - conRentCalc })
  }
  // Conteo de colecciones cargadas (todos los agentes)
  log('info', 'datos cargados', {
    publicaciones: (datos.publicaciones || []).length,
    ventas30d: (datos.ventas30d || []).length,
    ads: (datos.adsItems || []).length,
    productos: (datos.productos || []).length,
    catalogoLinks: (datos.catalogoLinks || []).length,
    bitacora: (datos.productoBitacora || []).length,
  })

  // 4. Ejecutar clasificador
  // Pasamos los `umbrales` del agente al clasificador. Los clasificadores
  // viejos (publicidad, opiniones, preguntas) los ignoran (no usan umbrales
  // editables). El de postventa los lee — patrón nuevo, opt-in.
  const resultado = clasificadorDef.clasificar({ ...datos, umbrales: agente.umbrales || {} })

  // Conteo de pubs por sección post-clasificación.
  const conteoSecciones = {}
  for (const [sec, items] of Object.entries(resultado.secciones || {})) {
    conteoSecciones[sec] = (items || []).length
  }
  log('info', 'secciones devueltas por el clasificador', conteoSecciones)

  // 10-jul-2026 — declarar exclusiones del clasificador (que el número cierre):
  // cuarentena post-pausa, piso de clicks, umbral de generadores.
  if (resultado.excluidos) {
    const exc = resultado.excluidos
    // 15-jul-2026: además de los 3 campos históricos de Publicidad, se
    // declara TODO excluidos tal cual lo devolvió el clasificador — ahí
    // viven los contadores de los gates (sinEspacio, aPerdida,
    // adsGastanSinVender, venden, conPromoYa, …). El número tiene que cerrar.
    const escalares = {}
    for (const [k, v] of Object.entries(exc)) {
      if (v == null || typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') escalares[k] = v
      else if (Array.isArray(v)) escalares[k] = v.length
    }
    log('info', 'exclusiones del clasificador', {
      ...escalares,
      activarEnCuarentena: (exc.activarEnCuarentena || []).length,
      pisoClicks: exc.pisoClicks ?? null,
      umbralGeneradoresEfectivo: exc.umbralGeneradoresEfectivo ?? null,
    })
  }

  // Mayo 2026 — Debug específico del clasificador de Contribución Neta para
  // diagnosticar diferencia con dashboard. Si el clasificador dejó un debug
  // en window.__contribucionNetaDebug, lo incluimos en los logs UI.
  if (typeof window !== 'undefined' && window.__contribucionNetaDebugImpuestos) {
    log('info', 'debug clasificador IMPUESTOS (rates que llegan)', window.__contribucionNetaDebugImpuestos)
  }
  if (typeof window !== 'undefined' && Array.isArray(window.__contribucionNetaDebug) && window.__contribucionNetaDebug.length > 0) {
    log('info', 'debug clasificador ITEMS_DEBUG (lineas que pasaron por items investigados)', window.__contribucionNetaDebug)
  }

  // 5. Buscar tareas activas previas (para deduplicar). DEDUP CROSS-AGENTE
  // — para cada sección del agente, miramos si existe alguna tarea activa
  // con ese mismo `tipoAccion` (independiente del agente que la creó). El
  // bloqueo es a nivel (tipoAccion + itemId): si una pub ya tiene tarea
  // activa para ese tipoAccion, se filtra del recurso de la nueva tarea.
  // Si TODAS las pubs candidatas de la sección ya tienen tarea, omite la
  // sección entera (no crea nada).
  const ESTADOS_ACTIVOS = ['propuesto', 'pendiente', 'no_iniciada', 'en_curso', 'en_revision', 'pausada']
  // Resolvemos el tipoAccion EFECTIVO por sección — IGUAL que el modal
  // (CrearAccionableModal:305): primero el codigo del tipo mapeado en el
  // engranaje (configuracion/mapeoSeccionesTareas → /tiposTarea.codigo),
  // fallback SECCION_DEFAULTS. FIX 10-jul-2026: antes el agente usaba SOLO el
  // default → si el admin mapeó un tipo con otro codigo (ej. revisar →
  // upside_potential), manual y agente persistían tipoAccion DISTINTO para la
  // misma sección y el dedupe cross-flujo nunca matcheaba → tareas duplicadas.
  const areaAgente = agente.area || 'publicidad'
  const tipoAccionPorSeccion = {}
  for (const sec of (agente.secciones || [])) {
    let codigo = null
    try {
      const tipoId = await obtenerTipoTarea(areaAgente, sec)
      if (tipoId) {
        const tipo = await getTipoTarea(tipoId)
        codigo = tipo?.codigo || null
      }
    } catch (err) {
      log('warn', `no pude resolver el tipo mapeado de "${sec}" — uso default`, { error: err?.message })
    }
    const efectivo = codigo || SECCION_DEFAULTS[sec]?.tipoAccion || null
    if (efectivo) tipoAccionPorSeccion[sec] = efectivo
  }
  log('info', 'tipoAccion efectivo por sección (mapeo → default)', tipoAccionPorSeccion)
  const tiposAccionAgente = [...new Set(Object.values(tipoAccionPorSeccion))]
  // Flywheel: el universo de la query suma los tipos OPUESTOS — para bloquear
  // el lado inversión cuando hay plata abierta, y para resolver (cancelar/
  // recortar) la tarea opuesta cuando el lado plata detecta.
  const tiposUniverso = [...new Set([
    ...tiposAccionAgente,
    ...tiposAccionAgente.flatMap(t => TIPOS_OPUESTOS[t] || []),
  ])]
  // Mapa: `${tipoAccion}__${itemId}` → tareaId existente.
  const itemsBloqueadosPorTipo = new Map()
  // Mapa: tipoAccion → tareaId más antigua activa (para reporte).
  const tareasActivasPorTipoAccion = {}
  // Mayo 2026 — Detalles de tareas bloqueantes para logs (debug usuario).
  // Permite ver exactamente qué tarea está bloqueando si Mariano cree que
  // no hay tareas pero el agente sí encuentra (típico: tarea zombie con
  // estado activo pero filtrada en UI por rol/responsable/temporal).
  const tareasBloqueantesPorTipo = new Map()  // tipoAccion → [{ id, titulo, estado, recursos: N, ...}]
  if (tiposUniverso.length > 0) {
    // Firestore `where in` máx 10 valores — con los opuestos sumados puede
    // pasarse: se consulta en CHUNKS de 10 y se juntan los docs.
    // Usamos `getDocsFromServer` para forzar fetch fresco del servidor y
    // evitar que el cache local de Firestore devuelva tareas que el usuario
    // acaba de eliminar pero aún están en cache.
    // FIX 13-jul: filtrar estado en JS. Dos cláusulas `in` (tipoAccion × estado)
    // generan producto de disyunciones (N×6) y Firestore corta en 30 — con 6
    // tipos ya son 36. Consultamos solo por tipoAccion y filtramos estado acá.
    const docsTareas = []
    for (let i = 0; i < tiposUniverso.length; i += 10) {
      const snapChunk = await getDocsFromServer(query(
        collection(db, COLLECTIONS.EMPRESAS, empresaId, 'tareas'),
        where('tipoAccion', 'in', tiposUniverso.slice(i, i + 10)),
      ))
      docsTareas.push(...snapChunk.docs)
    }
    for (const d of docsTareas) {
      const data = d.data()
      const tipo = data.tipoAccion
      if (!tipo) continue
      if (!ESTADOS_ACTIVOS.includes(data.estado)) continue
      if (!tareasActivasPorTipoAccion[tipo]) tareasActivasPorTipoAccion[tipo] = d.id
      const itemIds = []
      for (const r of (data.recursos || [])) {
        if (r?.itemId) {
          itemsBloqueadosPorTipo.set(`${tipo}__${String(r.itemId)}`, d.id)
          itemIds.push(String(r.itemId))
        }
      }
      const arr = tareasBloqueantesPorTipo.get(tipo) || []
      arr.push({
        tareaId: d.id,
        titulo: data.titulo || '(sin titulo)',
        estado: data.estado,
        responsable: data.responsableUserId || null,
        emisor: data.emisorUserId || null,
        recursosCount: (data.recursos || []).length,
        recursosItemIds: itemIds.slice(0, 15),
      })
      tareasBloqueantesPorTipo.set(tipo, arr)
    }
    // Log diagnóstico — siempre visible si encontramos algo, así Mariano
    // ve qué tareas están bloqueando aunque la UI no las muestre.
    for (const [tipo, arr] of tareasBloqueantesPorTipo.entries()) {
      log('info', `dedup — tareas activas encontradas para tipoAccion="${tipo}"`, { count: arr.length, tareas: arr })
    }
  }
  // Conservamos `tareasActivasPorSeccion` por compat con el resto de la
  // función (reporte de "omitidas"), pero ahora se llena distinto:
  // marcamos una sección como "omitida" SOLO si todos sus items quedan
  // filtrados por dedup. Esto se decide más abajo per-sección.
  const tareasActivasPorSeccion = {}

  // ── GATE INFERIDO RETROACTIVO (15-jul-2026) ─────────────────────────────
  // El clasificador declara en `gateBloqueados` los ítems que HOY no tienen
  // espacio (margen honesto con ads/impuestos bajo el mínimo, o ads gastando
  // sin vender). Si alguno tiene tarea ABIERTA del mismo tipo (creada con el
  // gate viejo, o cuando el margen todavía daba), el recurso se quita; si la
  // tarea queda vacía se CANCELA con motivo — nunca borrado seco (doctrina
  // flywheel 14-jul). Corre ANTES del loop de secciones: una sección puede
  // quedar con 0 candidatos y aun así tener zombies que resolver.
  if (resultado?.gateBloqueados && typeof resultado.gateBloqueados === 'object') {
    for (const [seccionGate, itemsGate] of Object.entries(resultado.gateBloqueados)) {
      if (!Array.isArray(itemsGate) || itemsGate.length === 0) continue
      if (Array.isArray(agente.secciones) && !agente.secciones.includes(seccionGate)) continue
      const tipoGate = tipoAccionPorSeccion[seccionGate] || SECCION_DEFAULTS[seccionGate]?.tipoAccion || null
      if (!tipoGate) continue
      const afectadas = new Map() // tareaId → Set(itemIds sin espacio)
      for (const itemIdRaw of itemsGate) {
        const itemId = String(itemIdRaw || '')
        if (!itemId) continue
        const tid = itemsBloqueadosPorTipo.get(`${tipoGate}__${itemId}`)
        if (tid) {
          if (!afectadas.has(tid)) afectadas.set(tid, new Set())
          afectadas.get(tid).add(itemId)
        }
      }
      for (const [tareaId, itemIdsConflicto] of afectadas) {
        try {
          const tRef = doc(db, COLLECTIONS.EMPRESAS, empresaId, 'tareas', tareaId)
          const tSnap = await getDoc(tRef)
          if (!tSnap.exists()) continue
          const tData = tSnap.data()
          if (!ESTADOS_ACTIVOS.includes(tData.estado)) continue
          const restantes = (tData.recursos || []).filter(r => !itemIdsConflicto.has(String(r?.itemId || r?.id || '')))
          const motivo = `Gate de rentabilidad inferido: ${itemIdsConflicto.size} ítem(s) ya no tienen espacio (margen honesto con ads e impuestos bajo el mínimo) — agente "${agente.nombre}".`
          if (restantes.length === 0) {
            await updateDoc(tRef, { estado: 'cancelada', canceladaEn: serverTimestamp(), motivoCancelacion: motivo, actualizadoEn: serverTimestamp() })
            log('info', `gate retroactivo: tarea ${tareaId} (${tData.tipoAccion}) CANCELADA con motivo — ningún recurso conserva espacio`, { items: [...itemIdsConflicto] })
          } else {
            await updateDoc(tRef, { recursos: restantes, actualizadoEn: serverTimestamp(), notaFlywheel: motivo })
            log('info', `gate retroactivo: ${itemIdsConflicto.size} recurso(s) quitados de la tarea ${tareaId} (${tData.tipoAccion}) — quedan ${restantes.length}`, { items: [...itemIdsConflicto] })
          }
          // Coherencia del dedupe para el resto de la corrida.
          for (const itemQuitado of itemIdsConflicto) itemsBloqueadosPorTipo.delete(`${tipoGate}__${itemQuitado}`)
        } catch (e) {
          log('warn', `no pude aplicar el gate retroactivo sobre la tarea ${tareaId}`, { error: e?.message })
        }
      }
    }
  }

  // 6. Crear tareas por sección (solo si no hay una activa previa)
  const destinatario = config.destinatarioUserId
  const seccionesConItems = []
  const casosSugeridos = [] // secciones con sugiereCaso: NO crean tarea, van a una tarea-resumen
  let totalTareas = 0
  let seccionesOmitidas = 0

  for (const [seccion, items] of Object.entries(resultado.secciones)) {
    if (!items || items.length === 0) continue

    // GATE por secciones del agente (10-jul-2026). Antes este loop procesaba
    // TODAS las secciones que devolviera el clasificador — las tildadas en el
    // agente solo alimentaban el dedup. Consecuencia doble: (a) el agente
    // creaba tareas de secciones NO tildadas, y (b) si agente.secciones tenía
    // claves viejas que ya no existen, el dedup no corría y se DUPLICABAN
    // tareas. Ahora: si el agente declara secciones, solo esas se procesan.
    // Un agente sin secciones declaradas (array vacío) no crea nada — mismo
    // contrato que documenta seccionesDisponibles en el registry.
    if (Array.isArray(agente.secciones) && !agente.secciones.includes(seccion)) {
      log('info', `sección "${seccion}" omitida — no está tildada en el agente`, { seccionesAgente: agente.secciones })
      continue
    }

    // Obtener tipo de tarea mapeado para esta sección
    const area = agente.area || 'publicidad'
    const tipoTareaId = await obtenerTipoTarea(area, seccion)

    // Defaults de la sección
    const defaults = SECCION_DEFAULTS[seccion]
    if (!defaults) continue // sección sin defaults = no crear tarea

    // Dedup cross-agente: filtrar los items que ya tienen tarea activa con
    // el mismo `tipoAccion` (sin importar qué agente la creó).
    const tipoAccionSeccion = tipoAccionPorSeccion[seccion] || defaults.tipoAccion
    // FIX 10-jul-2026 (duplicado manual↔agente): la clave de dedupe usaba
    // `item.id` PRIMERO, pero para los ads canónicos `id` es el docId de
    // campanasItems — mientras el flujo manual guarda el MLA (_itemId) en el
    // recurso. Claves distintas → el dedupe nunca matcheaba entre flujos y el
    // agente duplicaba tareas creadas a mano (y viceversa). El identificador
    // de negocio es SIEMPRE el externalId de la publicación: _itemId primero.
    const itemIdDe = (item) => String(item._itemId || item.item_id || item.itemId || item.id || '')
    // Flywheel: si la sección es del lado INVERSIÓN (prender/bajar) y el ítem
    // tiene tarea abierta del lado PLATA opuesto (pausar/subir), se bloquea —
    // nunca dos órdenes contradictorias vivas. Declarado en el log.
    const opuestosPlata = (TIPOS_OPUESTOS[tipoAccionSeccion] || []).filter(t => TIPOS_LADO_PLATA.has(t))
    let bloqueadosPorContradiccion = 0
    const itemsFiltrados = items.filter(item => {
      const itemId = itemIdDe(item)
      if (!itemId) return true   // sin itemId no podemos dedupear, dejamos pasar
      if (itemsBloqueadosPorTipo.has(`${tipoAccionSeccion}__${itemId}`)) return false
      for (const op of opuestosPlata) {
        if (itemsBloqueadosPorTipo.has(`${op}__${itemId}`)) { bloqueadosPorContradiccion++; return false }
      }
      return true
    })
    const cuantosBloqueados = items.length - itemsFiltrados.length
    if (bloqueadosPorContradiccion > 0) {
      log('info', `sección "${seccion}" — ${bloqueadosPorContradiccion} ítem(s) bloqueados por CONTRADICCIÓN (tarea abierta del lado plata: ${opuestosPlata.join('/')})`, { tipoAccion: tipoAccionSeccion })
    }

    // Flywheel: si la sección es del lado PLATA y hay tareas abiertas del lado
    // INVERSIÓN sobre los mismos ítems (ej. activar_ad abierta y ahora se
    // detecta pausar_ad), la plata manda RETROACTIVAMENTE: el recurso
    // conflictivo se quita de la tarea opuesta; si queda vacía, se CANCELA con
    // motivo (nunca borrado seco — decisión Mariano 14-jul-2026).
    if (TIPOS_LADO_PLATA.has(tipoAccionSeccion)) {
      const opuestosInversion = TIPOS_OPUESTOS[tipoAccionSeccion] || []
      const afectadas = new Map() // tareaId → Set(itemIds en conflicto)
      for (const item of itemsFiltrados) {
        const itemId = itemIdDe(item)
        if (!itemId) continue
        for (const op of opuestosInversion) {
          const tid = itemsBloqueadosPorTipo.get(`${op}__${itemId}`)
          if (tid) {
            if (!afectadas.has(tid)) afectadas.set(tid, new Set())
            afectadas.get(tid).add(itemId)
          }
        }
      }
      for (const [tareaOpuestaId, itemIdsConflicto] of afectadas) {
        try {
          const tRef = doc(db, COLLECTIONS.EMPRESAS, empresaId, 'tareas', tareaOpuestaId)
          const tSnap = await getDoc(tRef)
          if (!tSnap.exists()) continue
          const tData = tSnap.data()
          if (!ESTADOS_ACTIVOS.includes(tData.estado)) continue
          const restantes = (tData.recursos || []).filter(r => !itemIdsConflicto.has(String(r?.itemId || r?.id || '')))
          const motivo = `Contradicción resuelta por flywheel: se detectó ${tipoAccionSeccion} (la plata manda) para ${itemIdsConflicto.size} ítem(s) de esta tarea — agente "${agente.nombre}".`
          if (restantes.length === 0) {
            await updateDoc(tRef, { estado: 'cancelada', canceladaEn: serverTimestamp(), motivoCancelacion: motivo, actualizadoEn: serverTimestamp() })
            log('info', `contradicción: tarea opuesta ${tareaOpuestaId} (${tData.tipoAccion}) CANCELADA con motivo — todos sus recursos pasaron a ${tipoAccionSeccion}`, { items: [...itemIdsConflicto] })
          } else {
            await updateDoc(tRef, { recursos: restantes, actualizadoEn: serverTimestamp(), notaFlywheel: motivo })
            log('info', `contradicción: ${itemIdsConflicto.size} recurso(s) quitados de la tarea opuesta ${tareaOpuestaId} (${tData.tipoAccion}) — quedan ${restantes.length}`, { items: [...itemIdsConflicto] })
          }
        } catch (e) {
          log('warn', `no pude resolver la contradicción con la tarea ${tareaOpuestaId}`, { error: e?.message })
        }
      }
    }
    // Ficha del tipo (una sola lectura) — trae cuota y flags de comportamiento.
    // Nada hardcodeado en el motor: el merge y el trato de la cuota salen de
    // la ficha del tipo (misma filosofía que la rentabilidad mínima del producto).
    let tipoDoc = null
    try {
      tipoDoc = tipoTareaId ? await getTipoTarea(tipoTareaId) : await getTipoTareaPorCodigo(tipoAccionSeccion)
    } catch { /* sin catálogo accesible → defaults */ }
    const esMergeTipo = !!tipoDoc?.fusionaPorPublicacion
    // Secciones marcadas como "sugiere caso" (config del tipo): el agente NO crea
    // tarea estructural ni caso — junta los candidatos y crea UNA tarea-resumen.
    if (tipoDoc?.sugiereCaso) {
      casosSugeridos.push({ seccion, label: seccion.replace(/^cambiar_/, '').replace(/_/g, ' '), tipoCaso: tipoDoc.tipoCasoSugerido || null, items })
      log('info', `sección "${seccion}" — ${items.length} candidato(s) a caso (sugiereCaso): van a la tarea-resumen, no crea tarea`, { tipoAccion: tipoAccionSeccion })
      continue
    }
    // ── Merge-addon (Etapa 1, 13-jul-2026) ──────────────────────────────────
    // Las pubs BLOQUEADAS por dedup, para tipos que acumulan hallazgos de
    // varias fuentes (tipo.fusionaPorPublicacion), no se descartan: se SUMAN a la
    // tarea existente con un bloque por origen (el área del agente). Corre
    // ANTES del early-return de "todas bloqueadas" para no perder el merge, y
    // NO cuenta contra la cuota (es la misma pub, no agranda el backlog).
    if (esMergeTipo) {
      const bloqueados = items.filter(item => {
        const id = itemIdDe(item)
        return id && itemsBloqueadosPorTipo.has(`${tipoAccionSeccion}__${id}`)
      })
      if (bloqueados.length > 0) {
        try {
          const rMerge = await upsertRecursosEnTarea(empresaId, {
            tipoAccion: tipoAccionSeccion,
            origen: areaAgente,
            recursos: bloqueados.map(item => ({
              itemId: itemIdDe(item),
              titulo: item._titulo || item.title || '',
              permalink: item._permalink || item.permalink || null,
              ...(item.hallazgos ? { hallazgos: item.hallazgos } : {}),
              ...(item.metricas ? { metricas: item.metricas } : {}),
            })),
          })
          log('info', `sección "${seccion}" — merge-addon de ${bloqueados.length} pub(s) en tarea existente (origen ${areaAgente})`, rMerge)
        } catch (e) {
          log('warn', `sección "${seccion}" — merge-addon falló, sigo`, { error: e?.message })
        }
      }
    }

    if (itemsFiltrados.length === 0) {
      // Todas las pubs ya tienen tarea activa con ese tipoAccion → omitir.
      const tareaEjemplo = tareasActivasPorTipoAccion[tipoAccionSeccion] || null
      const tareasBloqueantes = tareasBloqueantesPorTipo.get(tipoAccionSeccion) || []
      log('warn', `sección "${seccion}" omitida — todas las pubs ya tienen tarea activa`, {
        items: items.length,
        tipoAccion: tipoAccionSeccion,
        tareasBloqueantes: tareasBloqueantes.map(t => ({ id: t.tareaId, titulo: t.titulo, estado: t.estado, recursos: t.recursosCount })),
      })
      seccionesOmitidas++
      seccionesConItems.push({ seccion, items: items.length, tareaId: tareaEjemplo, omitida: true, motivo: 'dedup_cross_agente' })
      tareasActivasPorSeccion[seccion] = tareaEjemplo
      continue
    }
    if (cuantosBloqueados > 0) {
      log('info', `sección "${seccion}" — ${cuantosBloqueados} pub(s) filtradas por dedup, ${itemsFiltrados.length} continúan`, { tipoAccion: tipoAccionSeccion })
    }

    // ── Cuota de producción (11-jul-2026, catálogo v1) ──────────────────────
    // Máximo de ítems por tarea generada. Regla (Mariano): mientras exista una
    // tarea ABIERTA del mismo tipoAccion no se genera otro lote — al cerrarse,
    // la próxima corrida toma los N siguientes en el orden en que el
    // clasificador entrega la sección — NO todos ordenan por prioridad (report
    // scoEqWsgJX991pQovyvL). Como las secciones que comparten tipo (ej. foto con/sin
    // tráfico) comparten la cuota, los quick wins drenan primero y el backlog
    // espera su turno.
    // ⭐ 14-jul-2026 (decisión Mariano): la cuota es SOLO el valor seteado en
    // pantalla para ESTA empresa (empresa.cuotasProduccion[tipoAccion], badge 🏭
    // del dashboard). Sin valor seteado = SIN LÍMITE (queda el cap técnico de
    // 300 recursos/tarea como paracaídas). El default del catálogo
    // (tipo.cuotaProduccion) DEJÓ DE APLICAR — ya no hay cascada.
    let cuotaProduccion = null
    const cuotaEmpresa = Number(datos?.config?.cuotasProduccion?.[tipoAccionSeccion])
    if (cuotaEmpresa > 0) cuotaProduccion = cuotaEmpresa
    // CAP DE SEGURIDAD (12-jul-2026): Firestore rechaza docs > 1MB — una tarea
    // con miles de recursos no se puede escribir (visto en vivo: 4.101 recursos
    // = 1,16MB en Matriz/publicitar). Si el tipo NO tiene cuota de producción y
    // la sección junta más que el tope, se comporta COMO SI tuviera cuota =
    // tope: sale en lotes y no genera otro mientras haya uno abierto.
    const CAP_RECURSOS_TAREA = 300
    let cuotaEfectiva = cuotaProduccion
    if (!cuotaEfectiva && itemsFiltrados.length > CAP_RECURSOS_TAREA) {
      cuotaEfectiva = CAP_RECURSOS_TAREA
      log('info', `sección "${seccion}" — ${itemsFiltrados.length} ítems superan el tope por tarea (${CAP_RECURSOS_TAREA}): se aplica lote de seguridad`, { tipoAccion: tipoAccionSeccion })
    }
    let backlogTotal = null
    if (cuotaEfectiva) {
      const abiertas = tareasBloqueantesPorTipo.get(tipoAccionSeccion) || []
      // Los tipos que acumulan por publicación (fusionaPorPublicacion) NO se
      // frenan por "lote en curso": las pubs nuevas siguen creando tarea y las
      // ya existentes se fusionan (el merge-addon corrió más arriba).
      if (abiertas.length > 0 && !esMergeTipo) {
        log('info', `sección "${seccion}" omitida — cuota/lote: lote en curso para tipo "${tipoAccionSeccion}"`, {
          cuotaEfectiva,
          tareasAbiertas: abiertas.map(t => ({ id: t.tareaId, titulo: t.titulo, estado: t.estado, recursos: t.recursosCount })),
        })
        seccionesOmitidas++
        seccionesConItems.push({ seccion, items: items.length, omitida: true, motivo: 'cuota_lote_en_curso' })
        continue
      }
      if (itemsFiltrados.length > cuotaEfectiva) {
        backlogTotal = itemsFiltrados.length
        itemsFiltrados.splice(cuotaEfectiva) // recorta al lote: los primeros N en el orden del clasificador
        log('info', `sección "${seccion}" — lote de ${cuotaEfectiva} sobre ${backlogTotal} detectados`, { tipoAccion: tipoAccionSeccion })
      }
    }

    // preCrear de la sección (paridad con el modal — ej. Matriz/completar_datos
    // siembra los productos no vinculados antes de crear la tarea). Falla suave.
    if (typeof defaults.preCrear === 'function') {
      try {
        const r = await defaults.preCrear({ empresaId, items: itemsFiltrados })
        if (r?.sembradas > 0) log('info', `sección "${seccion}" — preCrear sembró ${r.sembradas} producto(s)`, r.resultado || {})
      } catch (e) {
        log('warn', `sección "${seccion}" — preCrear falló, la tarea se crea igual`, { error: e?.message })
      }
    }

    // Construir un recurso por cada item (formato compartido entre agrupado e individual).
    // FIX 10-jul-2026: itemId = el externalId de la publicación (_itemId),
    // NUNCA item.id primero (para ads canónicos es el docId de campanasItems).
    // Con esto el recurso del agente y el del flujo manual guardan LA MISMA
    // clave → dedupe cross-flujo y exclusión "ya en tarea" funcionan.
    const recursoGenericoDe = (item) => ({
      itemId: itemIdDe(item) || '',
      titulo: item._titulo || item.title || item.id || '',
      permalink: item._permalink || item.permalink || null,
      // Payload extra que el clasificador haya armado (ej. hallazgos de IA, métricas, banda)
      ...(item.hallazgos ? { hallazgos: item.hallazgos } : {}),
      ...(item.metricas ? { metricas: item.metricas } : {}),
      // Motivo del hallazgo (15-jul-2026, paridad con el flujo manual: el
      // modal ya lo guarda — las vistas de tareas lo muestran bajo el recurso).
      ...(item._motivo ? { _motivo: item._motivo } : {}),
      ...(item.tipo ? { tipo: item.tipo } : {}),
      ...(item.bandaKey ? { bandaKey: item.bandaKey } : {}),
      ...(item.tiempoPromMin != null ? { tiempoPromMin: item.tiempoPromMin } : {}),
      // Vínculo al producto del catálogo (para abrir ficha desde la tarea).
      ...(item._productoId ? { productoId: item._productoId } : {}),
      // Bloque de rentabilidad — solo se incluye si el clasificador lo armó
      // (hoy: Velocidad de Consumo en secciones activar_promo / activar_ad).
      // Permite que el operador decida cuánto descuento / ACOS aplicar sin
      // volver al dashboard. Misma fórmula que VelocidadConsumoDashboard.jsx.
      ...(item._precio != null || item._costoUnit != null ? {
        rentabilidad: {
          precio: item._precio ?? null,
          costoUnit: item._costoUnit ?? null,
          margenPct: item._margenPct ?? null,            // margen actual al precio actual
          netoTargetPct: item._netoTargetPct ?? null,    // neto promedio histórico de la cuenta
          espacioPct: item._espacioPct ?? null,          // cuánto podés gastar en promo/ad sin perder benchmark
          precioMinimoSugerido: item._precioMinimoSugerido ?? null,
        },
      } : {}),
      // Bloque de reposición — unidades sugeridas a comprar. Solo lo arman
      // los clasificadores de Velocidad de Consumo para secciones
      // reponer_quiebre y reponer_velocidad. La vista de la tarea muestra
      // este bloque en lugar del de rentabilidad cuando el tipoAccion es
      // de reposición.
      // Se arma cuando el clasificador DECLARA el pedido, aunque sea `null`:
      // sin ventana de ventas (report djynjlXICuUF0Yl803M8) velocidadConsumo
      // manda el quiebre con el pedido en null, y con `!= null` el bloque
      // entero —stock incluido— desaparecía de la tarea sin aviso.
      ...('_pedirMin' in item || '_pedirOk' in item ? {
        reposicion: {
          stock: item._stock ?? null,
          velocidadDiaria: item._velocidadDiaria ?? null,
          tiempoReposicionDias: item._tiempoReposicion ?? null,
          diasHastaQuiebre: Number.isFinite(item._diasHastaQuiebre) ? item._diasHastaQuiebre : null,
          unidadesAReponerMin: item._pedirMin ?? null,    // lo justo (1N días al recibir)
          unidadesAReponerOk: item._pedirOk ?? null,      // operación normal (2N días)
          objetivoMinDias: item._objetivoMinDias ?? null,
          objetivoOkDias: item._objetivoOkDias ?? null,
        },
      } : {}),
      // Bloque competitividad — para pubs en revisar_competitividad. Lleva
      // margen FINAL efectivo de las órdenes vendidas + precio sugerido
      // para igualar el promedio de la cuenta. No usamos margen bruto
      // sobre costo porque confunde (queda más alto que el target).
      ...((item._margenFinalOrdenes != null || item._precioSugerido != null) ? {
        competitividad: {
          margenFinalOrdenes: item._margenFinalOrdenes ?? null,
          ordenesCount: item._ordenesCount ?? null,
          netoTargetPct: item._netoTargetPct ?? null,
          motivoCompetitividad: item._motivoCompetitividad || null,
          precio: item._precio ?? null,
          costoUnit: item._costoUnit ?? null,
          precioSugerido: item._precioSugerido ?? null,
          deltaPrecio: item._deltaPrecio ?? null,
        },
      } : {}),
      completado: false,
    })
    // Contribución Neta persiste LA evidencia que persiste su vista (órdenes que
    // justifican la tarea): un solo escritor, en el clasificador. Los bloques
    // genéricos de arriba no la conocen y escribían campos vacíos (report
    // HXwwsN2cVVhPwP7A3k6V).
    const recursoDe = agente.dashboardId === 'ml-contribucion-neta'
      ? (item) => ({ ...recursoContribucionNeta(item), completado: false })
      : recursoGenericoDe

    const baseTareaData = {
      descripcion: `Tarea creada automáticamente por el agente "${agente.nombre}".`,
      prioridad: defaults.prioridad || 'media',
      // FIX 10-jul-2026: persistir el tipoAccion EFECTIVO (mapeo → default),
      // el mismo que persiste el modal — si difieren, el dedupe cross-flujo
      // no matchea y se duplican tareas manual↔agente.
      tipoAccion: tipoAccionSeccion || defaults.tipoAccion || seccion,
      tipoTareaId: tipoTareaId || null,
      emisorUserId: ejecutadoPor,
      responsableUserId: destinatario,
      ejecutorTipo: 'agente',
      ejecutorId: agenteId,
      area,
      origen: { tipo: 'agente', agenteId, agenteName: agente.nombre },
      origenDashboard: {
        modulo: area,
        seccion,
        empresaId,
        fechaAnalisis: new Date().toISOString().slice(0, 10),
      },
      estado: 'pendiente',
    }

    if (agente.modoTareas === 'individual') {
      // Una tarea por item — el operador resuelve uno a uno.
      const idsCreados = []
      for (const item of itemsFiltrados) {
        const recurso = recursoDe(item)
        // Tipos que acumulan por publicación: guardar el recurso con el
        // bloque por origen desde la creación, para que un merge posterior de
        // otro dashboard sume una 2ª fuente (chip "N fuentes").
        const recursoFinal = esMergeTipo ? recursoConOrigen(recurso, areaAgente) : recurso
        const tituloItem = typeof defaults.titulo === 'function'
          ? defaults.titulo(1)
          : `${seccion}: 1 item`
        const tituloFinal = `${tituloItem} — ${recurso.titulo || recurso.itemId}`.slice(0, 200)
        try {
          const id = await crearAccionable(empresaId, {
            ...baseTareaData,
            titulo: tituloFinal,
            recursos: [recursoFinal],
          })
          idsCreados.push(id)
        } catch (err) {
          log('error', `falló crear tarea individual en sección "${seccion}"`, { itemId: recurso.itemId, error: err?.message || String(err) })
          throw err
        }
      }
      log('info', `sección "${seccion}" — creadas ${idsCreados.length} tareas individuales`, { items: itemsFiltrados.length })
      seccionesConItems.push({ seccion, items: itemsFiltrados.length, tareaIds: idsCreados, modo: 'individual', dedupBloqueados: cuantosBloqueados })
      totalTareas += idsCreados.length
    } else {
      // Modo agrupado (default): 1 tarea con todos los items como recursos.
      let titulo = typeof defaults.titulo === 'function'
        ? defaults.titulo(itemsFiltrados.length)
        : `${seccion}: ${itemsFiltrados.length} items`
      if (backlogTotal) {
        titulo = `${titulo} (lote de ${itemsFiltrados.length} · backlog ${backlogTotal.toLocaleString('es-AR')})`
      }
      const recursos = itemsFiltrados.map(item => esMergeTipo ? recursoConOrigen(recursoDe(item), areaAgente) : recursoDe(item))
      // Adjuntos del agente (16-jul-2026 — tarea cargar-datos): secciones con
      // buildAdjuntosAgente en SECCION_DEFAULTS adjuntan archivos INLINE
      // generados de los items POST-dedupe/cuota (ej. el CSV para completar
      // costos). Paridad con buildAdjuntos del modal. Falla suave.
      let adjuntosAgente = null
      if (typeof defaults.buildAdjuntosAgente === 'function') {
        try {
          const a = defaults.buildAdjuntosAgente(itemsFiltrados)
          if (Array.isArray(a) && a.length > 0) adjuntosAgente = a
        } catch (e) {
          log('warn', `sección "${seccion}" — buildAdjuntosAgente falló, la tarea sale sin adjunto`, { error: e?.message })
        }
      }
      let tareaId
      try {
        tareaId = await crearAccionable(empresaId, {
          ...baseTareaData,
          ...(backlogTotal ? {
            descripcion: `Lote de ${itemsFiltrados.length} sobre ${backlogTotal.toLocaleString('es-AR')} detectados — el próximo lote se genera automáticamente al cerrar esta tarea. Tarea creada por el agente "${agente.nombre}".`,
            lote: { tamano: itemsFiltrados.length, backlogTotal, cuotaProduccion: cuotaEfectiva },
          } : {}),
          ...(adjuntosAgente ? { adjuntos: adjuntosAgente } : {}),
          titulo,
          recursos,
        })
      } catch (err) {
        log('error', `falló crear tarea agrupada en sección "${seccion}"`, { items: itemsFiltrados.length, error: err?.message || String(err) })
        throw err
      }
      log('info', `sección "${seccion}" — creada tarea agrupada con ${itemsFiltrados.length} recursos`, { tareaId })
      seccionesConItems.push({ seccion, items: itemsFiltrados.length, tareaId, modo: 'agrupado', dedupBloqueados: cuantosBloqueados })
      totalTareas++
    }
  }

  // 5.b Tarea-resumen de casos sugeridos (Postventa). El agente NUNCA abre casos:
  // crea una sola tarea que dice "tenés N situaciones para abrir caso". Dedup: si
  // ya hay una tarea-resumen abierta, no crea otra (se refresca al cerrarla).
  if (casosSugeridos.length > 0) {
    const totalCasos = casosSugeridos.reduce((n, c) => n + (c.items?.length || 0), 0)
    if (totalCasos > 0) {
      const TIPO_RESUMEN = 'abrir_casos_postventa'
      let yaHay = false
      try {
        const snapPrev = await getDocsFromServer(query(
          collection(db, COLLECTIONS.EMPRESAS, empresaId, 'tareas'),
          where('tipoAccion', '==', TIPO_RESUMEN),
          where('estado', 'in', ESTADOS_ACTIVOS),
        ))
        yaHay = !snapPrev.empty
      } catch (e) { log('warn', 'no pude chequear tarea-resumen de casos previa', { error: e?.message }) }
      if (yaHay) {
        log('info', `casos sugeridos: ${totalCasos} situación(es) — ya hay una tarea-resumen abierta, no creo otra`)
      } else {
        const desglose = casosSugeridos.filter(c => (c.items?.length || 0) > 0).map(c => `${c.items.length} ${c.label}`).join(', ')
        const recursos = casosSugeridos.flatMap(c => (c.items || []).map(it => {
          const nRec = it._reclamos || it?.metricas?.reclamosOtros || null
          return {
            itemId: String(it._itemId || it.item_id || it.itemId || it.id || ''),
            titulo: it._titulo || it.title || '',
            permalink: it._permalink || it.permalink || null,
            categoriaCaso: c.seccion,
            categoriaCasoLabel: c.label,
            tipoCasoSugerido: c.tipoCaso || null,
            problema: `${c.label}${nRec ? ` · ${nRec} reclamo${nRec > 1 ? 's' : ''}` : ''}${it._motivoTexto ? ` — ${it._motivoTexto}` : ''}`,
            motivo: it._motivoTexto || null,
            claimId: it._claimId || null,
            incidentes: it._incidentes || [],
            reclamoUrl: it._claimId ? `/empresas/${empresaId}?tab=dash-mercado-libre&dashId=ml-postventa&reclamo=${it._claimId}` : null,
            // Namespace de descarte alineado con el dashboard (otros/logistica/...).
            dismissKey: `${({ otras_problematicas: 'otros', cambiar_transportista: 'logistica', cambiar_proveedor: 'proveedor', cambiar_embalaje: 'embalaje' })[c.seccion] || c.seccion}:${String(it._itemId || it.item_id || it.itemId || it.id || '')}`,
            completado: false,
          }
        }))
        try {
          const tareaId = await crearAccionable(empresaId, {
            descripcion: `El agente detectó situaciones para evaluar como CASO (${desglose}). El agente no abre casos: entrá a Postventa → Detección de tareas y abrí cada uno a mano desde la publicación. Creada por el agente "${agente.nombre}".`,
            prioridad: 'alta',
            tipoAccion: TIPO_RESUMEN,
            tipoTareaId: null,
            emisorUserId: ejecutadoPor,
            responsableUserId: destinatario,
            ejecutorTipo: 'agente',
            ejecutorId: agenteId,
            area: areaAgente,
            origen: { tipo: 'agente', agenteId, agenteName: agente.nombre },
            origenDashboard: { modulo: areaAgente, seccion: 'abrir_casos', empresaId, fechaAnalisis: new Date().toISOString().slice(0, 10) },
            estado: 'pendiente',
            titulo: `Postventa: ${totalCasos} situaci${totalCasos > 1 ? 'ones' : 'ón'} para abrir caso`,
            recursos,
          })
          totalTareas++
          seccionesConItems.push({ seccion: 'abrir_casos', items: totalCasos, tareaId, modo: 'resumen_casos' })
          log('info', `casos sugeridos: creada tarea-resumen con ${totalCasos} situación(es) (${desglose})`, { tareaId })
        } catch (e) {
          log('error', 'falló crear la tarea-resumen de casos', { error: e?.message || String(e) })
        }
      }
    }
  }

  // 6. Registrar ejecución
  await registrarEjecucion(empresaId, agenteId, {
    tareasCreadas: totalTareas,
    secciones: seccionesConItems.map(s => s.seccion),
  })

  // Las del agente + las que el loader dio por perdidas + las que declaró el
  // clasificador (un insumo faltante, report djynjlXICuUF0Yl803M8). Viajan al
  // lado de `logs`, que es el canal de vuelta que ya existía (ADR 2026-08-13
  // decisión 5); lo nuevo es sólo la parte del clasificador que se suma acá.
  const { degradaciones, fuentes } = unirDegradaciones([regAgente.resultado(), degLoader, lecturasDelClasificador(resultado)])
  const fuentesIntentadas = fuentes.length
  if (degradaciones.length > 0) {
    log('warn', `corrida DEGRADADA: ${degradaciones.length} de ${fuentesIntentadas} fuente(s) no se pudieron leer`,
      degradaciones.map(d => `${d.fuente} (${d.motivo})`))
  }

  log('info', `END — ${totalTareas} tarea(s) creada(s), ${seccionesOmitidas} sección(es) omitida(s)`)

  return { tareasCreadas: totalTareas, seccionesOmitidas, secciones: seccionesConItems, logs, degradaciones, fuentes, fuentesIntentadas }
}

/**
 * Carga datos de Firestore para un clasificador
 * @param {string} empresaId
 * @param {Object} clasificadorDef - { colecciones, diasVentas }
 *
 * Devuelve, además de los datos, **la ventana con la que los cargó**:
 * `datos.ventanaVentasDias` (días) o `null` si no hay ventana de ventas.
 * Describe `ventas30d` —y sus aliases `ordenes`/`mlOrdenes`— **y nada más**:
 * un clasificador que divida un numerador DERIVADO (p. ej.
 * `_rentabilidadInferida.unidadesVendidas`, que lo calcula el backend con su
 * propia ventana) por este campo va a estar igual de mal que antes. Un
 * numerador derivado lleva su propio descriptor o no se divide.
 */
// ─── Bloque 7.6 — interceptores canónicos del loader ────────────────────────
// Los clasificadores declaran colecciones LEGACY en su `colecciones` (son ids
// semánticos); el loader las resuelve contra las CANÓNICAS y mapea al shape
// legacy en el borde — mismo patrón que los dashboards (7.3-7.5) y que el
// backend de rentabilidad. Los clasificadores no cambian (el refactor a
// camelCase nativo va con el retiro del legacy, Bloque 9).

async function leerPubsCanonicasLegacy(empresaId) {
  // Wave C C6c — `dealIds` salen de `facetas.enPromo` dentro de pubCanonicaALegacy
  // (faceta server canónica) → ya NO cruzamos `mlPublicacionesPromos` (lector
  // retirado). Una sola lectura.
  const pubsSnap = await getDocs(query(
    collection(db, 'publicaciones'),
    where('empresaId', '==', empresaId),
    where('canal', '==', 'mercado-libre'),
  ))
  return pubsSnap.docs.map(d => pubCanonicaALegacy(docConId(d)))
}

export async function cargarDatosParaClasificador(empresaId, clasificadorDef, { cache = null } = {}) {
  const colecciones = clasificadorDef.colecciones || []
  // `??` y no `||`: el 0 es un valor legítimo que significa "sin ventana de
  // ventas" (report bssopFE0UcL3dujEWO8k). Con `||` esos defs recibían 30 y el
  // guard de `:diasVentas > 0` nunca los salteaba, así que seis clasificadores
  // leían `ordenes` —lectura DURA, sin `conRegistro`— por un dato que ninguno
  // consume.
  const diasVentas = clasificadorDef.diasVentas ?? DIAS_VENTANA_DEFAULT

  const datos = { config: {} }

  // CARGA ÚNICA (16-jul-2026, agente general): si el caller pasa un `cache`
  // (Map compartido de la corrida), cada colección se lee UNA sola vez y
  // todos los agentes ven la MISMA foto de datos. Clave = colección (+ la
  // ventana y el filtro facturables para las ventas). Los docs cacheados se
  // COMPARTEN entre clasificadores — contrato: los clasificadores son
  // funciones puras y NO mutan sus inputs. Sin cache, comportamiento
  // idéntico al de siempre (corrida individual).
  const memo = (key, fn) => {
    if (!cache) return fn()
    if (!cache.has(key)) cache.set(key, fn())
    return cache.get(key)
  }

  // Cada unidad memoizada lleva SU registro y lo devuelve junto al valor: la
  // degradación se comparte con el cache en vez de quedarse en el agente que
  // la descubrió.
  const conRegistro = (fn) => async () => {
    const reg = registroDeLecturas()
    const r = await fn(reg)
    return { ...r, ...reg.resultado() }
  }

  // Cargar colecciones en paralelo — con interceptores canónicos (7.6).
  const cargarColeccion = async (col, reg) => {
    if (col === 'mlPublicaciones') {
      return { col, docs: await leerPubsCanonicasLegacy(empresaId) }
    }
    if (col === 'mlPublicacionesVisitas') {
      // La colección legacy quedó congelada — las visitas viven en el rollup
      // del doc canónico. Velocidad lee {itemId, _visitas30d}.
      const docs = await leerPubsCanonicasLegacy(empresaId)
      return { col, docs: docs.map(p => ({ itemId: p.itemId, id: p.itemId, _visitas30d: p._visitas30d })) }
    }
    if (col === 'catalogoLinks') {
      // CANÓNICA vinculosPublicacion mapeada al shape legacy (misma clase del
      // bug de rentabilidad del 3-jun: catalogoLinks quedó VACÍA post-seed —
      // los agentes veían todo sin_link).
      const snap = await getDocs(query(
        collection(db, 'vinculosPublicacion'),
        where('empresaId', '==', empresaId),
      ))
      return {
        col,
        docs: snap.docs.map(d => {
          const v = d.data()
          return {
            id: d.id,
            externalItemId: v.externalId ?? null,
            productoId: v.productoId ?? null,
            plataformaId: v.canal ?? null,
            cantidad: v.cantidad ?? 1,
          }
        }),
      }
    }
    if (col === 'mlReclamos') {
      // W3 tajada 6 — CANÓNICA `reclamos` al shape legacy que consume el
      // clasificador de postventa (itemIdDeReclamo/reclamoAbierto). Paridad
      // con el comportamiento legacy: universo COMPLETO de la empresa (sin
      // ventana — los reclamos son inherentemente pocos).
      const snap = await getDocs(query(
        collection(db, 'reclamos'),
        where('empresaId', '==', empresaId),
        where('canal', '==', 'mercado-libre'),
      ))
      return { col, docs: snap.docs.map(d => reclamoCanonicoALegacy({ _docId: d.id, ...d.data() })) }
    }
    if (col === 'mlDevoluciones') {
      // W3 tajada 6 — CANÓNICA `devoluciones` (entidad propia, D3) al shape
      // legacy (returnReason/claimId/status/dateClosed).
      const snap = await getDocs(query(
        collection(db, 'devoluciones'),
        where('empresaId', '==', empresaId),
        where('canal', '==', 'mercado-libre'),
      ))
      return { col, docs: snap.docs.map(d => devolucionCanonicaALegacy({ _docId: d.id, ...d.data() })) }
    }
    if (col === 'mlEnvios') {
      // CANÓNICA `envios` (Bloque 6). Shape canónico directo: el único
      // consumidor declarado (postventa) no lo usa todavía (param futuro).
      const snap = await getDocs(query(
        collection(db, 'envios'),
        where('empresaId', '==', empresaId),
        where('canal', '==', 'mercado-libre'),
      ))
      return { col, docs: snap.docs.map(docConId) }
    }
    if (col === 'mlOrdenes') {
      // Sin consumidor del histórico amplio (postventa usa el alias `ordenes`
      // = ventas30d). Marker: se aliasa a la ventana en los defaults — si un
      // clasificador futuro necesita histórico, que lo pida con rango propio.
      return { col, docs: null }
    }
    if (col === 'mlAds') {
      // Wave C C6g — universo per-ad desde la canónica VIVA (mlAds quedó
      // CONGELADA al morir syncMLAds): join campanasItems(subtipo ads) ⨝
      // campanasMetricas(refTipo publicacion) con el mapper compartido del
      // dashboard Publicidad (adsCanonicosALegacy → {id, item_id, campaign_id,
      // status, metrics}). El acosEstrategico per-ad se inyecta después desde
      // configPublicidad (ver _configPublicidad).
      const [ciSnap, cmSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'campanasItems'),
          where('empresaId', '==', empresaId),
          where('canal', '==', 'mercado-libre'),
          where('subtipo', '==', 'ads'),
        )),
        getDocs(query(
          collection(db, 'campanasMetricas'),
          where('empresaId', '==', empresaId),
          where('canal', '==', 'mercado-libre'),
          where('refTipo', '==', 'publicacion'),
        )),
      ])
      const docs = adsCanonicosALegacy(
        ciSnap.docs.map(d => ({ _docId: d.id, ...d.data() })),
        cmSnap.docs.map(d => d.data()),
      )
      return { col, docs }
    }
    if (col === 'mlCampanasAds') {
      // Wave C C6g — campañas desde la canónica `campanas` tipo='ads' al shape
      // legacy mínimo que consume analizarEficiencia ({campaign_id|id,
      // acosEstrategico}); el acosEstrategico se inyecta desde configPublicidad.
      const snap = await getDocs(query(
        collection(db, 'campanas'),
        where('empresaId', '==', empresaId),
        where('canal', '==', 'mercado-libre'),
        where('tipo', '==', 'ads'),
      ))
      return {
        col,
        docs: snap.docs.map(d => {
          const c = d.data()
          return {
            id: c.externalId != null ? String(c.externalId) : d.id,
            campaign_id: c.externalId != null ? String(c.externalId) : null,
            name: c.nombre ?? null,
            status: c.estado ?? null,
          }
        }),
      }
    }
    if (col === 'catalogoListas') {
      // Catálogo (11-jul-2026): sublistas precalculadas del rollup del advisor
      // de buy-box — mismas que lee el dashboard. El clasificador NO recalcula.
      const estados = ['conviene', 'al-limite', 'no-conviene', 'ganando', 'no-competible', 'no-aplica', 'sin-vincular', 'pendiente']
      const pares = await Promise.all(estados.map(async (e) => {
        const [par] = await reg.leer(`catalogoListas:${e}`, () =>
          getDoc(doc(db, 'dashboardRollups', claveRollup(empresaId, 'catalogo', CANAL_ML), 'listas', e))
            .then(snap => [e, snap.exists() ? (snap.data().items || []) : []]),
        [e, []])
        return par
      }))
      return { col, docs: Object.fromEntries(pares) }
    }
    if (col === 'mlPromos') {
      // Central de Promociones — master canónico `campanas` tipo='promo' →
      // shape legacy mlPromos (campanaPromoALegacyMaster). Respeta _visible.
      const snap = await getDocs(query(
        collection(db, 'campanas'),
        where('empresaId', '==', empresaId),
        where('canal', '==', 'mercado-libre'),
        where('tipo', '==', 'promo'),
      ))
      return { col, docs: snap.docs.map(d => campanaPromoALegacyMaster(docConId(d))).filter(Boolean) }
    }
    if (col === 'mlPublicacionesPromos') {
      // Central de Promociones — campanasItems (sin ads) agrupadas por
      // publicación al shape mlPublicacionesPromos (1 doc por pub con su
      // array promociones[]). Mismo adapter que useCentralPromosData.
      const snap = await getDocs(query(
        collection(db, 'campanasItems'),
        where('empresaId', '==', empresaId),
        where('canal', '==', 'mercado-libre'),
      ))
      return { col, docs: campanasItemsAPromosPorItem(snap.docs.map(docConId)) }
    }
    if (col === 'mlPublicacionesCentral') {
      // Publicaciones canónicas al shape de la Central (pubCanonicaALegacy +
      // familyId/conexionId/_syncedAt) — el clasificador agrupa perfil de
      // venta por familia y filtra multicuenta.
      const snap = await getDocs(query(
        collection(db, 'publicaciones'),
        where('empresaId', '==', empresaId),
        where('canal', '==', 'mercado-libre'),
      ))
      return { col, docs: snap.docs.map(d => pubCanonicaACentralPromos(docConId(d))).filter(Boolean) }
    }
    if (col === 'catalogoEventosCalendario') {
      // Calendario comercial (colección de plataforma, SIN empresaId) —
      // resuelve el lift por campaña en evaluarElegibilidadPromo.
      const [snap] = await reg.leer('catalogoEventosCalendario', () =>
        getDocs(query(collection(db, 'catalogoEventosCalendario'), where('paisId', '==', 'AR'))), null)
      return { col, docs: (snap?.docs || []).map(docConId) }
    }
    if (col === 'reglasCentralPromos') {
      // Reglas de Decisión central-promociones — MISMA cascada que
      // useReglasDecision: empresa → global → defaults hardcoded.
      // Si se caen, la cascada cae a `getDefaultsParametros`: el clasificador
      // NO decide sin un dato, decide con OTROS números. La consecuencia más
      // cara del reporte.
      const [[empSnap], [globSnap]] = await Promise.all([
        reg.leer('reglasDecision:empresa', () => getDoc(doc(db, 'empresas', empresaId, 'reglasDecision', 'central-promociones')), null),
        reg.leer('reglasDecision:catalogo', () => getDoc(doc(db, 'catalogoReglasDecision', 'central-promociones')), null),
      ])
      const defaults = getDefaultsParametros('central-promociones') || {}
      const glob = globSnap?.exists?.() ? (globSnap.data().parametros || null) : null
      const emp = empSnap?.exists?.() ? (empSnap.data().parametros || null) : null
      return { col, docs: { ...defaults, ...(glob || {}), ...(emp || {}) } }
    }
    if (col === 'tendenciasVeredictosTitulo') {
      // Cache del gate de compatibilidad IA — FAIL-SOFT: si las rules todavía
      // no se deployaron (o hay lag de permisos), seguimos sin veredictos:
      // las secciones de título quedan en "pendiente" (cero tareas), que es
      // la dirección segura del gate. Un permiso denegado acá NO debe abortar
      // la corrida del agente (las secciones exactas siguen funcionando).
      const [snap] = await reg.leer('tendenciasVeredictosTitulo', () =>
        getDocs(query(collection(db, 'tendenciasVeredictosTitulo'), where('empresaId', '==', empresaId))), null)
      return { col, docs: (snap?.docs || []).map(docConId) }
    }
    if (col === 'publicacionesTendencias') {
      // Tendencias (12-jul-2026) — pubs canónicas RAW (núcleo + datosCanal):
      // el clasificador lee categoryId/health/catalogo con fallbacks propios.
      const snap = await getDocs(query(
        collection(db, 'publicaciones'),
        where('empresaId', '==', empresaId),
        where('canal', '==', 'mercado-libre'),
      ))
      return { col, docs: snap.docs.map(docConId) }
    }
    if (col === 'mlFlexConfig') {
      // Doc per-conexión con coverage.byZone más reciente (mismo criterio que
      // la vista 🎯 de Flex). Los docs llevan empresaId en el payload.
      const [snap] = await reg.leer('flexConfig', () =>
        getDocs(query(collection(db, 'mlFlexConfig'), where('empresaId', '==', empresaId))), null)
      const docs = (snap?.docs || []).map(docConId)
      const conZonas = docs.filter(d => Array.isArray(d?.coverage?.byZone) && d.coverage.byZone.length > 0)
      const elegido = (conZonas.length > 0 ? conZonas : docs)
        .sort((a, b) => (b._syncedAt?.seconds || 0) - (a._syncedAt?.seconds || 0))[0] || null
      return { col, docs: elegido }
    }
    if (col === 'catalogoFull') {
      // Tarifas de Full vigentes (doc de plataforma, sin empresaId) — Estrategia Full.
      const [cat] = await reg.leer('catalogoFull', () => getFullVigente(), null)
      return { col, docs: cat }
    }
    if (col === 'rentabilidadEmpresa') {
      // empresas/{id}/config/rentabilidad — corte a nivel empresa (cascada ADR 032).
      const [snap] = await reg.leer('rentabilidadEmpresa', () =>
        getDoc(doc(db, 'empresas', empresaId, 'config', 'rentabilidad')), null)
      return { col, docs: snap?.exists?.() ? snap.data() : null }
    }
    if (PREFIJO_CANAL_SIN_INTERCEPTOR.test(col)) {
      // §2.B: un id de canal sin interceptor NO se lee. La rama de abajo lo
      // consultaría literal, y una colección de canal retirada no tiene
      // escritor: devolvería vacío sin error y el clasificador correría con
      // menos datos sin que nada lo diga. Se declara degradada, con nombre.
      const [docs] = await reg.leer(col, () => { throw new Error(MOTIVO_CANAL_SIN_INTERCEPTOR) }, [])
      return { col, docs }
    }
    const snap = await getDocs(query(collection(db, col), where('empresaId', '==', empresaId)))
    return { col, docs: snap.docs.map(docConId) }
  }
  const promises = colecciones.map((col) => memo(col, conRegistro((reg) => cargarColeccion(col, reg))))

  // Cargar empresa para config
  promises.push(memo('_empresa', () =>
    getDoc(doc(db, 'empresas', empresaId)).then(snap => ({
      col: '_empresa', docs: snap.exists() ? [docConId(snap)] : [],
    }))
  ))

  // Mayo 2026 — Cargar config fiscal para que el clasificador pueda usar la
  // cascada completa de rentabilidad (IVA + IIBB + percepciones + ganancias).
  // Mismas 3 fuentes que `useImpuestosEmpresa`: catalogoImpuestos + override
  // empresa + condicionFiscal. La condición se carga en cascada (depende del
  // override). Sin esto, el clasificador caería al cálculo simplificado.
  promises.push(memo('_impuestos', conRegistro(async (reg) => {
    const [[catSnap], [ovrSnap]] = await Promise.all([
      reg.leer('impuestos:catalogo', () => getDoc(doc(db, COLLECTIONS.CATALOGO_IMPUESTOS, 'AR')), null),
      reg.leer('impuestos:empresa', () => getDoc(doc(db, COLLECTIONS.EMPRESAS_IMPUESTOS, empresaId)), null),
    ])
    const catalogoPlataforma = catSnap?.exists?.() ? docConId(catSnap) : null
    const overrideEmpresa = ovrSnap?.exists?.() ? docConId(ovrSnap) : null
    let condicionFiscal = null
    if (overrideEmpresa?.condicionFiscalId) {
      // Tolerada, pero declarada: sin la condición fiscal la cascada calcula
      // con otra alícuota, no con "sin dato".
      const [cSnap] = await reg.leer('impuestos:condicionFiscal', () =>
        getDoc(doc(db, COLLECTIONS.CATALOGO_CONDICIONES_FISCALES, overrideEmpresa.condicionFiscalId)), null)
      if (cSnap?.exists?.()) condicionFiscal = docConId(cSnap)
    }
    return { col: '_impuestos', docs: [{ catalogoPlataforma, overrideEmpresa, condicionFiscal }] }
  })))

  // 19-ago-2026 — la lectura del agregado cross-vendedor SE BORRÓ. Desde el
  // gate del 4-ago `conocimientoEcosistema` es `read: if false`, así que este
  // `getDoc` devolvía `permission-denied` y el `.catch` lo convertía en `null`
  // sin una sola señal — incluido el runner, que corre este mismo bundle
  // autenticado como CONSULTOR y también pasa por rules.
  // Lo que la cascada necesita ahora se lo deposita el cron en el doc de la
  // empresa (`_costosML`), que `_empresa` ya carga acá al lado: una lectura
  // MENOS, y ningún tenant leyendo un doc cross-vendedor.
  // ADR `2026-08-19-el-conocimiento-del-ecosistema-llega-al-tenant-proyectado-en`.

  // Wave C C6g — ACOS estratégico desde la canónica `configPublicidad` (C6a:
  // los 3 niveles migraron ahí; los campos viejos en empresas/mlCampanasAds/
  // mlAds quedaron DEPRECADOS y no se leen — única fuente, sin fallback).
  // Pocos docs (solo overrides). Espejo de la reducción de useConfigPublicidad.
  if (colecciones.includes('mlAds') || colecciones.includes('mlCampanasAds')) {
    promises.push(memo('_configPublicidad', conRegistro(async (reg) => {
      const [docs] = await reg.leer('configPublicidad', () =>
        getDocs(query(collection(db, 'configPublicidad'), where('empresaId', '==', empresaId)))
          .then(snap => {
            const cfg = { general: null, porCampana: {}, porPublicacion: {} }
            snap.docs.forEach(d => {
              const x = d.data()
              const acos = x.acosEstrategico
              if (acos == null) return
              if (x.nivel === 'empresa') cfg.general = acos
              else if (x.nivel === 'campana' && x.refExternalId != null) cfg.porCampana[String(x.refExternalId)] = acos
              else if (x.nivel === 'publicacion' && x.refExternalId != null) cfg.porPublicacion[String(x.refExternalId)] = acos
            })
            return [cfg]
          }), [])
      return { col: '_configPublicidad', docs }
    })))
  }

  // Cargar ventas últimos N días — CANÓNICA `ordenes` con rango SERVER-SIDE
  // (Bloque 7.6). `fechaCreada` es SIEMPRE ISO string en la canónica, así que
  // el motivo del loaded-all legacy (date_created Timestamp-o-string) ya no
  // existe. Los docs salen por el adaptador espejo al shape legacy que los
  // clasificadores consumen, + compat del derivado `rentabilidad` persistido
  // (estadoOrden/rentaNetaTotal → estado/rentaNeta/publicidad que lee el
  // clasificador de contribución).
  if (diasVentas > 0) {
    const desde = inicioVentanaLocal(diasVentas)
    // Clave de cache con VENTANA + filtro facturables: dos clasificadores con
    // diasVentas distintos (30 vs 90 vs 215) NO comparten esta lectura.
    promises.push(memo(`_ventas:${diasVentas}:${clasificadorDef.soloFacturables ? 'fact' : 'all'}`, () =>

      getDocs(query(
        collection(db, 'ordenes'),
        where('empresaId', '==', empresaId),
        where('canal', '==', 'mercado-libre'),
        where('fechaCreada', '>=', desde.toISOString()),
      )).then(snap => {
        let canonicas = snap.docs.map(docConId)
        // Paridad de universo (12-jul-2026): el dashboard filtra esFacturable
        // (excluye canceladas/pendientes) ANTES de clasificar. Opt-in por
        // registry para no cambiar el universo de los demás clasificadores.
        if (clasificadorDef.soloFacturables) canonicas = canonicas.filter(esFacturable)
        const docs = canonicas.map(data => {
          const norm = ordenCanonicaAShapeCascada(data)
          const r = data.rentabilidad
          if (r && r.estadoOrden) {
            norm._rentabilidad = {
              ...r,
              estado: r.estadoOrden,
              rentaNeta: r.rentaNetaTotal,
              publicidad: Object.values(r.porLinea || {})
                .reduce((s, l) => s + (Number(l?.publicidad) || 0), 0),
            }
          }
          return norm
        })
        return { col: '_ventas', docs }
      })
    ))
  }

  const results = await Promise.all(promises)

  // Viene DENTRO del valor cacheado, así que dos agentes que comparten la
  // misma promesa declaran los dos la misma fuente caída.
  const deLasUnidades = unirDegradaciones(results)

  for (const { col, docs } of results) {
    if (col === '_empresa') {
      datos.config = docs[0] || {}
    } else if (col === '_impuestos') {
      // Mergeamos los 3 docs (plataforma + empresa + condicionFiscal) con
      // la misma función pura que usa el hook useImpuestosEmpresa.
      const { catalogoPlataforma, overrideEmpresa, condicionFiscal } = docs[0] || {}
      datos.impuestosEmpresa = mergearImpuestos({ catalogoPlataforma, overrideEmpresa, condicionFiscal })
    } else if (col === '_ventas') {
      datos.ventas30d = docs
    } else if (col === 'mlAds') {
      datos.adsItems = docs
    } else if (col === 'mlPublicaciones') {
      datos.publicaciones = docs
    } else if (col === 'mlCampanasAds') {
      datos.campanas = docs
    } else if (col === '_configPublicidad') {
      datos._acosCfg = docs[0] || null
    } else if (col === 'kbItems') {
      datos.kbItems = docs
    } else if (col === 'kbOpinionesItems') {
      datos.kbOpinionesItems = docs
    } else if (col === 'dashPreguntasMetricas') {
      // Tomamos el doc más reciente (orden por fechaCorrida desc).
      const sorted = [...docs].sort((a, b) =>
        (b.fechaCorrida || '').localeCompare(a.fechaCorrida || '')
      )
      datos.dashMetricas = sorted[0] || null
    } else if (col === 'dashOpinionesMetricas') {
      // Cada clasificador recibe `datos.dashMetricas` como el rollup de SU
      // módulo. Si ya hay (porque otro clasificador del mismo agente lo cargó)
      // no lo pisamos.
      const sorted = [...docs].sort((a, b) =>
        (b.fechaCorrida || '').localeCompare(a.fechaCorrida || '')
      )
      datos.dashMetricas = datos.dashMetricas || sorted[0] || null
    } else if (col === 'mlReclamos') {
      // Postventa MVP F6 bloque 2 — alias semánticos.
      datos.reclamos = docs
    } else if (col === 'mlDevoluciones') {
      datos.devoluciones = docs
    } else if (col === 'mlEnvios') {
      datos.envios = docs
    } else if (col === 'mlOrdenes') {
      // docs viene null del interceptor (7.6) — el histórico amplio no tiene
      // consumidor; el alias se resuelve a ventas30d en los Defaults.
      if (docs) datos.mlOrdenes = docs
    } else if (col === 'catalogoListas') {
      datos.catalogoListas = docs
    } else if (col === 'catalogoFull') {
      datos.catalogoFull = docs || null
    } else if (col === 'mlFlexConfig') {
      datos.flexConfig = docs || null
    } else if (col === 'rentabilidadEmpresa') {
      datos.rentabilidadEmpresa = docs || null
    } else if (col === 'mlPromos') {
      datos.promosCampanias = docs
    } else if (col === 'mlPublicacionesPromos') {
      datos.promosPorItem = docs
    } else if (col === 'mlPublicacionesCentral') {
      datos.publicacionesCentral = docs
    } else if (col === 'catalogoEventosCalendario') {
      datos.eventosCalendario = docs
    } else if (col === 'reglasCentralPromos') {
      datos.reglasPromo = docs || null
    } else {
      datos[col] = docs
    }
  }

  // Defaults
  datos.adsItems = datos.adsItems || []
  datos.publicaciones = datos.publicaciones || []
  datos.ventas30d = datos.ventas30d || []
  datos.campanas = datos.campanas || []
  datos.kbItems = datos.kbItems || []
  // Defaults conservadores (RI Arg) si la empresa no tiene config fiscal.
  if (!datos.impuestosEmpresa) {
    datos.impuestosEmpresa = mergearImpuestos({
      catalogoPlataforma: null, overrideEmpresa: null, condicionFiscal: null,
    })
  }
  // `ecosistema` ahora sale de la PROYECCIÓN que el cron depositó en el doc de
  // la empresa, no de una lectura al agregado cross-vendedor (que es
  // `read: if false` desde el 4-ago), con su `siteId` para las bandas.
  // `datos.config` es el doc de empresa, cargado arriba por `_empresa`.
  datos.ecosistema = datos.config?._costosML ?? null
  datos.kbOpinionesItems = datos.kbOpinionesItems || []
  datos.dashMetricas = datos.dashMetricas || null
  // Postventa MVP F6 bloque 2 — defaults para las claves nuevas.
  datos.reclamos = datos.reclamos || []
  datos.devoluciones = datos.devoluciones || []
  datos.envios = datos.envios || []
  // Alias `ordenes` apunta a `ventas30d` (canónica filtrada por ventana).
  datos.ordenes = datos.ventas30d
  datos.mlOrdenes = datos.mlOrdenes || datos.ventas30d

  // ─── La ventana VIAJA con los datos (report djynjlXICuUF0Yl803M8) ───────
  // El numerador (las unidades de `ventas30d`) sale de esta ventana; hasta este
  // campo, el divisor lo adivinaba cada clasificador por su cuenta —
  // `umbrales.ventanaDias` del doc del agente, o una constante local— y los dos
  // números coincidían SÓLO porque los cuatro defs afectados declaran 30. El día
  // que uno de los dos lados se mueva, la velocidad diaria sale escalada por el
  // cociente de las dos ventanas, sin error, sin log y sin ninguna señal.
  //
  // `null` y no 30 cuando no hay ventana: el `0` del registry significa "no
  // cargues ventas" (6 defs lo declaran) y acá no hay ningún número correcto por
  // el que dividir. Mismo molde que `fechaCorteReclamos` más abajo, que es de
  // esta misma clase: el "sin dato" es de primera clase y el consumidor lo
  // declara, en vez de recibir un default que miente. Inventar un 30 acá es
  // exactamente el defecto que este campo viene a cerrar.
  datos.ventanaVentasDias = diasVentas > 0 ? diasVentas : null
  // Corte de la ventana del cooldown de bitácora, para la def que la declara
  // (`cooldownBitacoraDias`): el clasificador es puro y no lee el reloj. Mismo
  // molde que `fechaCorteReclamos`; sin declaración, `null` (sin cooldown).
  datos.desdeCooldownMs = clasificadorDef.cooldownBitacoraDias > 0
    ? Date.now() - clasificadorDef.cooldownBitacoraDias * 24 * 60 * 60 * 1000
    : null

  // Wave C C6g — inyección del ACOS estratégico canónico (configPublicidad)
  // en los shapes legacy que consume resolveAcosRef (publicidad.js): per-ad
  // (nivel publicacion), per-campaña (nivel campana) y general (nivel empresa,
  // pisa el campo DEPRECADO del doc empresa — única fuente, sin fallback).
  if (datos._acosCfg) {
    const { general, porCampana, porPublicacion } = datos._acosCfg
    datos.adsItems = datos.adsItems.map(ad => ({
      ...ad,
      acosEstrategico: porPublicacion[String(ad.item_id)] ?? null,
    }))
    datos.campanas = datos.campanas.map(c => ({
      ...c,
      acosEstrategico: porCampana[String(c.campaign_id ?? c.id)] ?? null,
    }))
    datos.config = { ...datos.config, acosEstrategico: general }
    delete datos._acosCfg
  }


  // Tendencias (12-jul-2026): resolver tendencias con el MISMO callable
  // cache-first que usa el dashboard (refTendencias, TTL semanal). Depende de
  // las publicaciones ya cargadas → corre después del Promise.all. siteId MVP:
  // MLA (multipaís se parametriza con el ADR correspondiente).
  // Estas cuatro NO son memoizadas: corren una vez por agente.
  const regFinal = registroDeLecturas()

  if (colecciones.includes('refTendencias')) {
    datos.tendenciasPorCategoria = {}
    datos.tendenciasPais = { keywords: [] }
    const categorias = categoriasDistintas(datos.publicacionesTendencias || [])
    if (categorias.length > 0) {
      const [t, degTendencias] = await regFinal.leer('tendencias', async () => {
        const functions = getFunctions(firebaseApp, 'us-central1')
        const fn = httpsCallable(functions, 'resolverTendencias')
        const res = await fn({ empresaId, siteId: 'MLA', categoryIds: [...categorias, 'ALL'] })
        return res?.data?.tendencias || {}
      }, null)
      if (degTendencias) {
        logLoader.warn('resolverTendencias falló — Tendencias sigue sin keywords', { error: degTendencias.motivo })
      } else {
        datos.tendenciasPais = t.ALL || { keywords: [] }
        const porCat = { ...t }
        delete porCat.ALL
        datos.tendenciasPorCategoria = porCat
      }
    }
  }

  // Mapeo global reason→categoría (mismo que el dashboard de Postventa) para
  // que "otras problemáticas" del agente coincida con lo que ve el operador.
  if (clasificadorDef.necesitaMapeoReason) {
    const [mapeo, degMapeo] = await regFinal.leer('mapeoReasonCategoria', async () => {
      const snap = await getDocs(collection(db, 'mapeoReasonCategoria'))
      const m = {}
      snap.forEach(d => { const data = d.data() || {}; m[d.id] = data.categoria || null })
      return m
    }, {})
    datos.mapeoReason = mapeo
    if (degMapeo) logLoader.warn('mapeoReasonCategoria no accesible', { error: degMapeo.motivo })
    // Nombres de motivo (mlClaimReasons global) para el contexto del caso.
    const [reasons, degReasons] = await regFinal.leer('claimReasons', async () => {
      const snapR = await getDocs(collection(db, 'mlClaimReasons'))
      const rn = {}
      snapR.forEach(d => { const data = d.data() || {}; rn[d.id] = data.descripcion || data.name || null })
      return rn
    }, {})
    datos.mlClaimReasons = reasons
    if (degReasons) logLoader.warn('mlClaimReasons no accesible', { error: degReasons.motivo })
    // Corte por fecha para "otras problemáticas": mismo período que el dashboard
    // (diasVentas). El agente carga reclamos sin ventana; acá lo acotamos.
    // Con `diasVentas === 0` ("sin ventana") el corte tiene que ser `null`, que
    // es el "sin corte" de primera clase del clasificador (`postventa.js`, el
    // default del parámetro). La cuenta directa daría `Date.now() - 0` = ahora,
    // que es TRUTHY: `postventa.js` lo trataría como filtro real y descartaría
    // TODOS los reclamos, en silencio. Hasta el report bssopFE0UcL3dujEWO8k
    // esto lo tapaba el `|| 30` de arriba; al honrar el 0, el guard pasa a ser
    // necesario.
    datos.fechaCorteReclamos = diasVentas > 0 ? Date.now() - (diasVentas * 24 * 60 * 60 * 1000) : null
    // Situaciones descartadas por el operador — el agente NO las vuelve a
    // sugerir. Hasta el report f0MNIPLik esta lectura no logueaba NADA: si se
    // caía, el agente re-sugería lo ya descartado sin traza. La peor de las mudas.
    const [dismissed, degDismissed] = await regFinal.leer('dismissedCasos', async () => {
      const empSnap = await getDoc(doc(db, 'empresas', empresaId))
      return empSnap.exists() ? (empSnap.data()?.postventaConfig?.dismissedCasos || {}) : {}
    }, {})
    datos.dismissedCasos = dismissed
    if (degDismissed) {
      logLoader.warn('dismissedCasos no accesible — el agente puede re-sugerir situaciones ya descartadas', { error: degDismissed.motivo })
    }
  }

  // Campo con `_` (molde `_acosCfg`): no cambia la firma de esta función ni
  // el contrato del clasificador. Lo consume y lo borra `ejecutarAgente`.
  datos._degradaciones = unirDegradaciones([deLasUnidades, regFinal.resultado()])

  return datos
}

// ═══════════════════════════════════════
// Modelos de IA disponibles (para selector)
// ═══════════════════════════════════════

export const MODELOS_IA = [
  { id: 'claude-sonnet-4', nombre: 'Claude Sonnet 4', provider: 'anthropic' },
  { id: 'claude-opus-4', nombre: 'Claude Opus 4', provider: 'anthropic' },
  { id: 'claude-haiku-3.5', nombre: 'Claude Haiku 3.5', provider: 'anthropic' },
  { id: 'gpt-4o', nombre: 'GPT-4o', provider: 'openai' },
  { id: 'gpt-4o-mini', nombre: 'GPT-4o Mini', provider: 'openai' },
]

// Debug
if (typeof window !== 'undefined') {
  window.__agentes = { listarAgentes, obtenerAgente, upsertAgente, listarAgentesEmpresa, configurarAgenteEmpresa }
}
